import type { NamespaceCatalog } from '@/i18n/ns';

const companySettingsCopy = {
  en: {
    companyProfile: 'Company profile',
    profileUnavailable: 'Your company profile is not available.',
    profileDescription: 'The starting context for everyone working with Poneva at {company}.',
    companyName: 'Company name',
    website: 'Website',
    primaryMarket: 'Primary market',
    companyLanguage: 'Company language',
    companyMatters: 'What matters to your company',
    profileHint: 'A useful starting point, not a restriction on what you can ask.',
    reloadCompany: 'Reload company',
    saving: 'Saving…',
    saveCompanyProfile: 'Save company profile',
    profileSaved: 'Company profile saved',
    team: 'Team',
    teamDescription: 'The people you work with, and what they can publish.',
    loadTeam: 'Load team',
    people: 'People',
    member: 'Member',
    role: 'Role',
    companyPublishing: 'Company publishing',
    teamPrivacy:
      'Chats and drafts stay private until shared. Publishing permission never grants access to someone else’s private work.',
    inviteColleague: 'Invite a colleague',
    inviteIntro: 'One invitation, one verified email address.',
    emailAddress: 'Email address',
    invitationPlaceholder: 'colleague@company.com',
    admin: 'Admin',
    createInvitation: 'Create invitation link',
    invitationShare:
      'Share the generated link with your colleague. Invitations expire after seven days.',
    invitationLink: 'Invitation link',
    publishingPermission: 'Publishing permission',
    canPublish: 'Can publish',
    needsApproval: 'Needs approval',
    needsApprovalDefault: 'Needs approval · default',
    canPublishDefault: 'Can publish · default',
    save: 'Save',
    savePermissions: 'Save permissions for {email}',
    owner: 'Owner',
    transferOwnership: 'Transfer ownership',
    removeAccess: 'Remove access',
    expires: 'expires',
    processing: 'AI processing',
    processingIntro:
      'Choose where Poneva’s AI runs for everyone at {company}: chat, voice, meetings and agents.',
    loading: 'Loading…',
    retry: 'Try again',
    whatChanges: 'What changes',
    currentChoice: 'Current choice',
    current: 'Current',
    keepEu: 'Keep all AI processing in the EU',
    useGlobal: 'Use the best models worldwide',
    euBody: 'For strict data rules. A few newer models and voices aren’t available in the EU yet.',
    globalBody: 'The strongest models and voices, processed in the EU or elsewhere.',
    saveProcessing: 'Save',
    savingProcessing: 'Saving…',
    processingSaved: 'Saved',
    setupAria: 'Keep building your workspace',
    setupMoreTitle: 'More context. More possibilities.',
    setupFirstTitle: 'Your tools make this more useful.',
    setupConnected: 'Connections: {count}. Bring another part of your work into the conversation.',
    setupFirstBody: 'Connect the tools you use to work with your own information.',
    setupAdd: 'Add another connection',
    setupFirst: 'Connect your first tool',
    setupExplore: 'Explore available connections',
    setupDismiss: 'Dismiss connection suggestion',
    personalMemory: 'Personal memory',
    personalMemoryDescription:
      'Preferences and context specific to you. What the whole company should know belongs on its Knowledge pages.',
    preferencesSaved: 'Preferences saved',
    companyInviteTitle: 'Company settings',
    companyInviteDescription:
      'Your account needs a company invitation. Contact Poneva to connect your account.',
  },
  bg: {
    companyProfile: 'Профил на компанията',
    profileUnavailable: 'Профилът на компанията ви не е наличен.',
    profileDescription: 'Началният контекст за всички, които работят с Poneva в {company}.',
    companyName: 'Име на компанията',
    website: 'Уебсайт',
    primaryMarket: 'Основен пазар',
    companyLanguage: 'Език на компанията',
    companyMatters: 'Какво е важно за компанията ви',
    profileHint: 'Полезна отправна точка, а не ограничение за това, което можете да питате.',
    reloadCompany: 'Презареждане на компанията',
    saving: 'Запазване…',
    saveCompanyProfile: 'Запазване на профила на компанията',
    profileSaved: 'Профилът на компанията е запазен',
    team: 'Екип',
    teamDescription: 'Хората, с които работите, и какво могат да публикуват.',
    loadTeam: 'Зареждане на екипа',
    people: 'Хора',
    member: 'Член',
    role: 'Роля',
    companyPublishing: 'Публикуване за компанията',
    teamPrivacy:
      'Чатовете и черновите остават лични, докато не бъдат споделени. Разрешението за публикуване никога не дава достъп до чужда лична работа.',
    inviteColleague: 'Покана към колега',
    inviteIntro: 'Една покана, един потвърден имейл адрес.',
    emailAddress: 'Имейл адрес',
    invitationPlaceholder: 'colleague@company.com',
    admin: 'Администратор',
    createInvitation: 'Създаване на линк за покана',
    invitationShare: 'Споделете генерирания линк с колегата си. Поканите изтичат след седем дни.',
    invitationLink: 'Линк за покана',
    publishingPermission: 'Разрешение за публикуване',
    canPublish: 'Може да публикува',
    needsApproval: 'Изисква одобрение',
    needsApprovalDefault: 'Изисква одобрение · по подразбиране',
    canPublishDefault: 'Може да публикува · по подразбиране',
    save: 'Запазване',
    savePermissions: 'Запазване на разрешенията за {email}',
    owner: 'Собственик',
    transferOwnership: 'Прехвърляне на собствеността',
    removeAccess: 'Премахване на достъпа',
    expires: 'изтича',
    processing: 'Обработка с AI',
    processingIntro:
      'Изберете къде да работи AI на Poneva за всички в {company}: чат, глас, срещи и агенти.',
    loading: 'Зареждане…',
    retry: 'Опитайте отново',
    whatChanges: 'Какво се променя',
    currentChoice: 'Текущ избор',
    current: 'Текущо',
    keepEu: 'Цялата обработка с AI да остане в ЕС',
    useGlobal: 'Използване на най-добрите модели по света',
    euBody:
      'За строги правила за данните. Няколко по-нови модела и гласа все още не са налични в ЕС.',
    globalBody: 'Най-мощните модели и гласове, обработвани в ЕС или на друго място.',
    saveProcessing: 'Запазване',
    savingProcessing: 'Запазване…',
    processingSaved: 'Запазено',
    setupAria: 'Продължете да изграждате работното си пространство',
    setupMoreTitle: 'Повече контекст. Повече възможности.',
    setupFirstTitle: 'Инструментите ви правят това още по-полезно.',
    setupConnected: 'Връзки: {count}. Включете още една част от работата си в разговора.',
    setupFirstBody:
      'Свържете инструментите, които използвате, за да работите със собствената си информация.',
    setupAdd: 'Добавяне на още една връзка',
    setupFirst: 'Свържете първия си инструмент',
    setupExplore: 'Разглеждане на наличните връзки',
    setupDismiss: 'Отхвърляне на предложението за връзка',
    personalMemory: 'Лична памет',
    personalMemoryDescription:
      'Предпочитания и контекст, свързани с вас. Това, което трябва да знае цялата компания, принадлежи на страниците ѝ в Knowledge.',
    preferencesSaved: 'Предпочитанията са запазени',
    companyInviteTitle: 'Настройки на компанията',
    companyInviteDescription:
      'Акаунтът ви се нуждае от покана за компанията. Свържете се с Poneva, за да свържете акаунта си.',
  },
  hr: {
    companyProfile: 'Profil tvrtke',
    profileUnavailable: 'Profil vaše tvrtke nije dostupan.',
    profileDescription: 'Početni kontekst za sve koji rade s Ponevom u tvrtki {company}.',
    companyName: 'Naziv tvrtke',
    website: 'Web-stranica',
    primaryMarket: 'Primarno tržište',
    companyLanguage: 'Jezik tvrtke',
    companyMatters: 'Što je važno vašoj tvrtki',
    profileHint: 'Korisna početna točka, a ne ograničenje onoga što možete pitati.',
    reloadCompany: 'Ponovno učitaj tvrtku',
    saving: 'Spremanje…',
    saveCompanyProfile: 'Spremi profil tvrtke',
    profileSaved: 'Profil tvrtke je spremljen',
    team: 'Tim',
    teamDescription: 'Osobe s kojima radite i ono što mogu objaviti.',
    loadTeam: 'Učitaj tim',
    people: 'Osobe',
    member: 'Član',
    role: 'Uloga',
    companyPublishing: 'Objavljivanje u tvrtki',
    teamPrivacy:
      'Razgovori i nacrti ostaju privatni dok ih ne podijelite. Dozvola za objavljivanje nikad ne daje pristup tuđem privatnom radu.',
    inviteColleague: 'Pozovi kolegu',
    inviteIntro: 'Jedna pozivnica, jedna potvrđena adresa e-pošte.',
    emailAddress: 'Adresa e-pošte',
    invitationPlaceholder: 'colleague@company.com',
    admin: 'Administrator',
    createInvitation: 'Izradi poveznicu pozivnice',
    invitationShare:
      'Podijelite generiranu poveznicu s kolegom. Pozivnice istječu nakon sedam dana.',
    invitationLink: 'Poveznica pozivnice',
    publishingPermission: 'Dozvola za objavljivanje',
    canPublish: 'Može objavljivati',
    needsApproval: 'Potrebno odobrenje',
    needsApprovalDefault: 'Potrebno odobrenje · zadano',
    canPublishDefault: 'Može objavljivati · zadano',
    save: 'Spremi',
    savePermissions: 'Spremi dozvole za {email}',
    owner: 'Vlasnik',
    transferOwnership: 'Prenesi vlasništvo',
    removeAccess: 'Ukloni pristup',
    expires: 'istječe',
    processing: 'AI obrada',
    processingIntro:
      'Odaberite gdje će Ponevin AI raditi za sve u tvrtki {company}: u razgovorima, glasovnim pozivima, sastancima i agentima.',
    loading: 'Učitavanje…',
    retry: 'Pokušaj ponovno',
    whatChanges: 'Što se mijenja',
    currentChoice: 'Trenutačni odabir',
    current: 'Trenutačno',
    keepEu: 'Zadrži svu AI obradu u EU-u',
    useGlobal: 'Upotrijebi najbolje modele iz cijelog svijeta',
    euBody:
      'Za stroga pravila o podacima. Nekoliko novijih modela i glasova još nije dostupno u EU-u.',
    globalBody: 'Najjači modeli i glasovi, obrađeni u EU-u ili drugdje.',
    saveProcessing: 'Spremi',
    savingProcessing: 'Spremanje…',
    processingSaved: 'Spremljeno',
    setupAria: 'Nastavite izgrađivati svoj radni prostor',
    setupMoreTitle: 'Više konteksta. Više mogućnosti.',
    setupFirstTitle: 'Vaši alati čine ovo korisnijim.',
    setupConnected: 'Veze: {count}. Uključite još jedan dio svog rada u razgovor.',
    setupFirstBody: 'Povežite alate koje upotrebljavate za rad s vlastitim informacijama.',
    setupAdd: 'Dodaj još jednu vezu',
    setupFirst: 'Poveži svoj prvi alat',
    setupExplore: 'Istraži dostupne veze',
    setupDismiss: 'Odbaci prijedlog za povezivanje',
    personalMemory: 'Osobna memorija',
    personalMemoryDescription:
      'Postavke i kontekst koji se odnose samo na vas. Ono što treba znati cijela tvrtka pripada njezinim stranicama znanja.',
    preferencesSaved: 'Postavke su spremljene',
    companyInviteTitle: 'Postavke tvrtke',
    companyInviteDescription:
      'Vašem je računu potrebna pozivnica za tvrtku. Obratite se Ponevi kako biste povezali svoj račun.',
  },
  cs: {
    companyProfile: 'Profil společnosti',
    profileUnavailable: 'Profil vaší společnosti není k dispozici.',
    profileDescription:
      'Výchozí kontext pro všechny, kdo ve společnosti {company} pracují s Ponevou.',
    companyName: 'Název společnosti',
    website: 'Web',
    primaryMarket: 'Hlavní trh',
    companyLanguage: 'Jazyk společnosti',
    companyMatters: 'Na čem vaší společnosti záleží',
    profileHint: 'Užitečný výchozí bod, nikoli omezení toho, na co se můžete ptát.',
    reloadCompany: 'Načíst společnost znovu',
    saving: 'Ukládání…',
    saveCompanyProfile: 'Uložit profil společnosti',
    profileSaved: 'Profil společnosti uložen',
    team: 'Tým',
    teamDescription: 'Lidé, se kterými pracujete, a co mohou publikovat.',
    loadTeam: 'Načíst tým',
    people: 'Lidé',
    member: 'Člen',
    role: 'Role',
    companyPublishing: 'Publikování za společnost',
    teamPrivacy:
      'Chaty a koncepty zůstávají soukromé, dokud je nesdílíte. Oprávnění k publikování nikdy neuděluje přístup k soukromé práci někoho jiného.',
    inviteColleague: 'Pozvat kolegu',
    inviteIntro: 'Jedna pozvánka, jedna ověřená e-mailová adresa.',
    emailAddress: 'E-mailová adresa',
    invitationPlaceholder: 'kolega@spolecnost.cz',
    admin: 'Správce',
    createInvitation: 'Vytvořit odkaz s pozvánkou',
    invitationShare: 'Sdílejte vygenerovaný odkaz se svým kolegou. Pozvánky vyprší po sedmi dnech.',
    invitationLink: 'Odkaz s pozvánkou',
    publishingPermission: 'Oprávnění k publikování',
    canPublish: 'Může publikovat',
    needsApproval: 'Vyžaduje schválení',
    needsApprovalDefault: 'Vyžaduje schválení · výchozí',
    canPublishDefault: 'Může publikovat · výchozí',
    save: 'Uložit',
    savePermissions: 'Uložit oprávnění pro {email}',
    owner: 'Vlastník',
    transferOwnership: 'Převést vlastnictví',
    removeAccess: 'Odebrat přístup',
    expires: 'vyprší',
    processing: 'Zpracování AI',
    processingIntro:
      'Vyberte, kde bude AI Ponevy pracovat pro všechny ve společnosti {company}: v chatu, hlasových hovorech, schůzkách a agentech.',
    loading: 'Načítání…',
    retry: 'Zkusit znovu',
    whatChanges: 'Co se změní',
    currentChoice: 'Aktuální volba',
    current: 'Aktuální',
    keepEu: 'Zpracovávat všechnu AI v EU',
    useGlobal: 'Používat nejlepší modely po celém světě',
    euBody:
      'Pro přísná pravidla pro data. Několik novějších modelů a hlasů zatím není v EU k dispozici.',
    globalBody: 'Nejvýkonnější modely a hlasy zpracovávané v EU nebo jinde.',
    saveProcessing: 'Uložit',
    savingProcessing: 'Ukládání…',
    processingSaved: 'Uloženo',
    setupAria: 'Pokračujte ve vytváření svého pracovního prostoru',
    setupMoreTitle: 'Více kontextu. Více možností.',
    setupFirstTitle: 'Vaše nástroje vám usnadní práci.',
    setupConnected: 'Připojení: {count}. Přidejte do konverzace další část své práce.',
    setupFirstBody: 'Připojte nástroje, které používáte k práci s vlastními informacemi.',
    setupAdd: 'Přidat další připojení',
    setupFirst: 'Připojit první nástroj',
    setupExplore: 'Prozkoumat dostupná připojení',
    setupDismiss: 'Zavřít návrh připojení',
    personalMemory: 'Osobní paměť',
    personalMemoryDescription:
      'Předvolby a kontext specifické pro vás. To, co by měla znát celá společnost, patří na její stránky znalostí.',
    preferencesSaved: 'Předvolby uloženy',
    companyInviteTitle: 'Nastavení společnosti',
    companyInviteDescription:
      'Váš účet vyžaduje pozvánku do společnosti. Kontaktujte Ponevu a propojte svůj účet.',
  },
  da: {
    companyProfile: 'Virksomhedsprofil',
    profileUnavailable: 'Din virksomhedsprofil er ikke tilgængelig.',
    profileDescription: 'Udgangspunktet for alle, der arbejder med Poneva hos {company}.',
    companyName: 'Virksomhedsnavn',
    website: 'Websted',
    primaryMarket: 'Primært marked',
    companyLanguage: 'Virksomhedssprog',
    companyMatters: 'Hvad er vigtigt for din virksomhed',
    profileHint: 'Et nyttigt udgangspunkt, ikke en begrænsning for, hvad du kan spørge om.',
    reloadCompany: 'Genindlæs virksomhed',
    saving: 'Gemmer…',
    saveCompanyProfile: 'Gem virksomhedsprofil',
    profileSaved: 'Virksomhedsprofil gemt',
    team: 'Team',
    teamDescription: 'De personer, du arbejder sammen med, og hvad de kan udgive.',
    loadTeam: 'Indlæs team',
    people: 'Personer',
    member: 'Medlem',
    role: 'Rolle',
    companyPublishing: 'Virksomhedsudgivelse',
    teamPrivacy:
      'Chats og kladder forbliver private, indtil de deles. Udgivelsestilladelse giver aldrig adgang til andres private arbejde.',
    inviteColleague: 'Inviter en kollega',
    inviteIntro: 'Én invitation, én bekræftet e-mailadresse.',
    emailAddress: 'E-mailadresse',
    invitationPlaceholder: 'colleague@company.com',
    admin: 'Administrator',
    createInvitation: 'Opret invitationslink',
    invitationShare:
      'Del det genererede link med din kollega. Invitationer udløber efter syv dage.',
    invitationLink: 'Invitationslink',
    publishingPermission: 'Udgivelsestilladelse',
    canPublish: 'Kan udgive',
    needsApproval: 'Kræver godkendelse',
    needsApprovalDefault: 'Kræver godkendelse · standard',
    canPublishDefault: 'Kan udgive · standard',
    save: 'Gem',
    savePermissions: 'Gem tilladelser for {email}',
    owner: 'Ejer',
    transferOwnership: 'Overfør ejerskab',
    removeAccess: 'Fjern adgang',
    expires: 'udløber',
    processing: 'AI-behandling',
    processingIntro:
      'Vælg, hvor Ponevas AI kører for alle hos {company}: chat, tale, møder og agenter.',
    loading: 'Indlæser…',
    retry: 'Prøv igen',
    whatChanges: 'Hvad ændrer sig',
    currentChoice: 'Aktuelt valg',
    current: 'Aktuelt',
    keepEu: 'Bevar al AI-behandling i EU',
    useGlobal: 'Brug de bedste modeller i hele verden',
    euBody:
      'Til strenge datakrav. Nogle nyere modeller og stemmer er endnu ikke tilgængelige i EU.',
    globalBody: 'De stærkeste modeller og stemmer, behandlet i EU eller andre steder.',
    saveProcessing: 'Gem',
    savingProcessing: 'Gemmer…',
    processingSaved: 'Gemt',
    setupAria: 'Fortsæt med at opbygge dit arbejdsområde',
    setupMoreTitle: 'Mere kontekst. Flere muligheder.',
    setupFirstTitle: 'Dine værktøjer gør dette mere nyttigt.',
    setupConnected: 'Forbindelser: {count}. Bring endnu en del af dit arbejde ind i samtalen.',
    setupFirstBody: 'Forbind de værktøjer, du bruger til at arbejde med dine egne oplysninger.',
    setupAdd: 'Tilføj endnu en forbindelse',
    setupFirst: 'Forbind dit første værktøj',
    setupExplore: 'Udforsk tilgængelige forbindelser',
    setupDismiss: 'Afvis forslag til forbindelse',
    personalMemory: 'Personlig hukommelse',
    personalMemoryDescription:
      'Præferencer og kontekst, der er specifikke for dig. Det, som hele virksomheden skal vide, hører hjemme på dens videnssider.',
    preferencesSaved: 'Præferencer gemt',
    companyInviteTitle: 'Virksomhedsindstillinger',
    companyInviteDescription:
      'Din konto kræver en virksomheds­invitation. Kontakt Poneva for at forbinde din konto.',
  },
  nl: {
    companyProfile: 'Bedrijfsprofiel',
    profileUnavailable: 'Je bedrijfsprofiel is niet beschikbaar.',
    profileDescription: 'De startcontext voor iedereen die met Poneva werkt bij {company}.',
    companyName: 'Bedrijfsnaam',
    website: 'Website',
    primaryMarket: 'Primaire markt',
    companyLanguage: 'Bedrijfstaal',
    companyMatters: 'Wat belangrijk is voor je bedrijf',
    profileHint: 'Een nuttig startpunt, geen beperking van wat je kunt vragen.',
    reloadCompany: 'Bedrijf opnieuw laden',
    saving: 'Opslaan…',
    saveCompanyProfile: 'Bedrijfsprofiel opslaan',
    profileSaved: 'Bedrijfsprofiel opgeslagen',
    team: 'Team',
    teamDescription: 'De mensen met wie je werkt en wat ze kunnen publiceren.',
    loadTeam: 'Team laden',
    people: 'Personen',
    member: 'Lid',
    role: 'Rol',
    companyPublishing: 'Publiceren namens het bedrijf',
    teamPrivacy:
      'Chats en concepten blijven privé totdat ze worden gedeeld. Publicatierechten geven nooit toegang tot het privéwerk van iemand anders.',
    inviteColleague: 'Een collega uitnodigen',
    inviteIntro: 'Eén uitnodiging, één geverifieerd e-mailadres.',
    emailAddress: 'E-mailadres',
    invitationPlaceholder: 'collega@bedrijf.nl',
    admin: 'Beheerder',
    createInvitation: 'Uitnodigingslink maken',
    invitationShare:
      'Deel de gegenereerde link met je collega. Uitnodigingen verlopen na zeven dagen.',
    invitationLink: 'Uitnodigingslink',
    publishingPermission: 'Publicatierechten',
    canPublish: 'Kan publiceren',
    needsApproval: 'Goedkeuring vereist',
    needsApprovalDefault: 'Goedkeuring vereist · standaard',
    canPublishDefault: 'Kan publiceren · standaard',
    save: 'Opslaan',
    savePermissions: 'Rechten opslaan voor {email}',
    owner: 'Eigenaar',
    transferOwnership: 'Eigendom overdragen',
    removeAccess: 'Toegang verwijderen',
    expires: 'verloopt',
    processing: 'AI-verwerking',
    processingIntro:
      'Kies waar de AI van Poneva wordt uitgevoerd voor iedereen bij {company}: chats, spraak, vergaderingen en agents.',
    loading: 'Laden…',
    retry: 'Opnieuw proberen',
    whatChanges: 'Wat verandert er',
    currentChoice: 'Huidige keuze',
    current: 'Huidig',
    keepEu: 'Alle AI-verwerking in de EU houden',
    useGlobal: 'De beste modellen wereldwijd gebruiken',
    euBody:
      'Voor strenge dataregels. Enkele nieuwere modellen en stemmen zijn nog niet beschikbaar in de EU.',
    globalBody: 'De krachtigste modellen en stemmen, verwerkt in de EU of elders.',
    saveProcessing: 'Opslaan',
    savingProcessing: 'Opslaan…',
    processingSaved: 'Opgeslagen',
    setupAria: 'Je workspace verder opbouwen',
    setupMoreTitle: 'Meer context. Meer mogelijkheden.',
    setupFirstTitle: 'Je tools maken dit nuttiger.',
    setupConnected: 'Verbindingen: {count}. Breng nog een onderdeel van je werk in het gesprek.',
    setupFirstBody: 'Verbind de tools die je gebruikt om met je eigen informatie te werken.',
    setupAdd: 'Nog een verbinding toevoegen',
    setupFirst: 'Je eerste tool verbinden',
    setupExplore: 'Beschikbare verbindingen bekijken',
    setupDismiss: 'Suggestie voor verbinding sluiten',
    personalMemory: 'Persoonlijk geheugen',
    personalMemoryDescription:
      "Voorkeuren en context die specifiek voor jou zijn. Wat het hele bedrijf moet weten, hoort op de Kennis-pagina's.",
    preferencesSaved: 'Voorkeuren opgeslagen',
    companyInviteTitle: 'Bedrijfsinstellingen',
    companyInviteDescription:
      'Je account heeft een bedrijfsuitnodiging nodig. Neem contact op met Poneva om je account te koppelen.',
  },
  et: {
    companyProfile: 'Ettevõtte profiil',
    profileUnavailable: 'Teie ettevõtte profiil pole saadaval.',
    profileDescription: 'Algne kontekst kõigile, kes kasutavad Ponevat ettevõttes {company}.',
    companyName: 'Ettevõtte nimi',
    website: 'Veebisait',
    primaryMarket: 'Peamine turg',
    companyLanguage: 'Ettevõtte keel',
    companyMatters: 'Mis on teie ettevõttele oluline',
    profileHint: 'Kasulik lähtekoht, mitte piirang sellele, mida küsida saate.',
    reloadCompany: 'Laadi ettevõte uuesti',
    saving: 'Salvestamine…',
    saveCompanyProfile: 'Salvesta ettevõtte profiil',
    profileSaved: 'Ettevõtte profiil on salvestatud',
    team: 'Meeskond',
    teamDescription: 'Inimesed, kellega töötate, ja see, mida nad avaldada saavad.',
    loadTeam: 'Laadi meeskond',
    people: 'Inimesed',
    member: 'Liige',
    role: 'Roll',
    companyPublishing: 'Ettevõtte avaldamine',
    teamPrivacy:
      'Vestlused ja mustandid jäävad privaatseks, kuni neid jagatakse. Avaldamisõigus ei anna kunagi juurdepääsu kellegi teise privaatsele tööle.',
    inviteColleague: 'Kutsu kolleeg',
    inviteIntro: 'Üks kutse, üks kinnitatud e-posti aadress.',
    emailAddress: 'E-posti aadress',
    invitationPlaceholder: 'colleague@company.com',
    admin: 'Administraator',
    createInvitation: 'Loo kutselink',
    invitationShare: 'Jagage loodud linki oma kolleegiga. Kutsed aeguvad seitsme päeva pärast.',
    invitationLink: 'Kutselink',
    publishingPermission: 'Avaldamisõigus',
    canPublish: 'Võib avaldada',
    needsApproval: 'Vajab heakskiitu',
    needsApprovalDefault: 'Vajab heakskiitu · vaikimisi',
    canPublishDefault: 'Võib avaldada · vaikimisi',
    save: 'Salvesta',
    savePermissions: 'Salvesta õigused aadressile {email}',
    owner: 'Omanik',
    transferOwnership: 'Anna omandiõigus üle',
    removeAccess: 'Eemalda juurdepääs',
    expires: 'aegub',
    processing: 'AI-töötlus',
    processingIntro:
      'Valige, kus Poneva AI töötab kõigi jaoks ettevõttes {company}: vestluses, hääles, koosolekutel ja agentides.',
    loading: 'Laadimine…',
    retry: 'Proovi uuesti',
    whatChanges: 'Mis muutub',
    currentChoice: 'Praegune valik',
    current: 'Praegune',
    keepEu: 'Hoia kogu AI-töötlus EL-is',
    useGlobal: 'Kasuta maailma parimaid mudeleid',
    euBody:
      'Rangete andmereeglite järgimiseks. Mõned uuemad mudelid ja hääled pole EL-is veel saadaval.',
    globalBody: 'Kõige võimekamad mudelid ja hääled, mida töödeldakse EL-is või mujal.',
    saveProcessing: 'Salvesta',
    savingProcessing: 'Salvestamine…',
    processingSaved: 'Salvestatud',
    setupAria: 'Jätka oma tööruumi loomist',
    setupMoreTitle: 'Rohkem konteksti. Rohkem võimalusi.',
    setupFirstTitle: 'Sinu tööriistad muudavad selle kasulikumaks.',
    setupConnected: 'Ühendused: {count}. Too veel üks osa oma tööst vestlusesse.',
    setupFirstBody: 'Ühenda tööriistad, mida kasutad oma teabega töötamiseks.',
    setupAdd: 'Lisa veel üks ühendus',
    setupFirst: 'Ühenda oma esimene tööriist',
    setupExplore: 'Tutvu saadaolevate ühendustega',
    setupDismiss: 'Loobu ühenduse soovitusest',
    personalMemory: 'Isiklik mälu',
    personalMemoryDescription:
      'Teile omased eelistused ja kontekst. See, mida kogu ettevõte peaks teadma, kuulub teie teadmiste lehtedele.',
    preferencesSaved: 'Eelistused on salvestatud',
    companyInviteTitle: 'Ettevõtte seaded',
    companyInviteDescription:
      'Teie konto vajab ettevõtte kutset. Konto ühendamiseks võtke ühendust Ponevaga.',
  },
  fi: {
    companyProfile: 'Yrityksen profiili',
    profileUnavailable: 'Yritysprofiilisi ei ole saatavilla.',
    profileDescription:
      'Aloituskonteksti kaikille, jotka työskentelevät Ponevan kanssa yrityksessä {company}.',
    companyName: 'Yrityksen nimi',
    website: 'Verkkosivusto',
    primaryMarket: 'Päämarkkina-alue',
    companyLanguage: 'Yrityksen kieli',
    companyMatters: 'Mikä on yrityksellenne tärkeää',
    profileHint: 'Hyödyllinen lähtökohta, ei rajoitus sille, mitä voit kysyä.',
    reloadCompany: 'Lataa yritys uudelleen',
    saving: 'Tallennetaan…',
    saveCompanyProfile: 'Tallenna yrityksen profiili',
    profileSaved: 'Yrityksen profiili tallennettu',
    team: 'Tiimi',
    teamDescription: 'Ihmiset, joiden kanssa työskentelet, ja mitä he voivat julkaista.',
    loadTeam: 'Lataa tiimi',
    people: 'Henkilöt',
    member: 'Jäsen',
    role: 'Rooli',
    companyPublishing: 'Yrityksen julkaiseminen',
    teamPrivacy:
      'Keskustelut ja luonnokset pysyvät yksityisinä, kunnes ne jaetaan. Julkaisuoikeus ei koskaan anna pääsyä jonkun muun yksityiseen työhön.',
    inviteColleague: 'Kutsu työtoveri',
    inviteIntro: 'Yksi kutsu, yksi vahvistettu sähköpostiosoite.',
    emailAddress: 'Sähköpostiosoite',
    invitationPlaceholder: 'colleague@company.com',
    admin: 'Ylläpitäjä',
    createInvitation: 'Luo kutsulinkki',
    invitationShare:
      'Jaa luotu linkki työtoverillesi. Kutsut vanhenevat seitsemän päivän kuluttua.',
    invitationLink: 'Kutsulinkki',
    publishingPermission: 'Julkaisuoikeus',
    canPublish: 'Voi julkaista',
    needsApproval: 'Vaatii hyväksynnän',
    needsApprovalDefault: 'Vaatii hyväksynnän · oletus',
    canPublishDefault: 'Voi julkaista · oletus',
    save: 'Tallenna',
    savePermissions: 'Tallenna käyttäjän {email} oikeudet',
    owner: 'Omistaja',
    transferOwnership: 'Siirrä omistajuus',
    removeAccess: 'Poista käyttöoikeus',
    expires: 'vanhenee',
    processing: 'Tekoälyn käsittely',
    processingIntro:
      'Valitse, missä Ponevan tekoäly toimii kaikille yrityksessä {company}: keskusteluissa, puheessa, kokouksissa ja agenteissa.',
    loading: 'Ladataan…',
    retry: 'Yritä uudelleen',
    whatChanges: 'Mikä muuttuu',
    currentChoice: 'Nykyinen valinta',
    current: 'Nykyinen',
    keepEu: 'Pidä kaikki tekoälyn käsittely EU:ssa',
    useGlobal: 'Käytä maailman parhaita malleja',
    euBody:
      'Tiukkoja tietosuojasääntöjä varten. Muutamat uudemmat mallit ja äänet eivät ole vielä saatavilla EU:ssa.',
    globalBody: 'Tehokkaimmat mallit ja äänet, joita käsitellään EU:ssa tai muualla.',
    saveProcessing: 'Tallenna',
    savingProcessing: 'Tallennetaan…',
    processingSaved: 'Tallennettu',
    setupAria: 'Jatka työtilasi rakentamista',
    setupMoreTitle: 'Enemmän kontekstia. Enemmän mahdollisuuksia.',
    setupFirstTitle: 'Työkalusi tekevät tästä hyödyllisemmän.',
    setupConnected: 'Yhteyksiä: {count}. Tuo toinen osa työtäsi keskusteluun.',
    setupFirstBody: 'Yhdistä työkalut, joita käytät omien tietojesi käsittelyyn.',
    setupAdd: 'Lisää toinen yhteys',
    setupFirst: 'Yhdistä ensimmäinen työkalusi',
    setupExplore: 'Tutustu saatavilla oleviin yhteyksiin',
    setupDismiss: 'Ohita yhteysehdotus',
    personalMemory: 'Henkilökohtainen muisti',
    personalMemoryDescription:
      'Sinua koskevat asetukset ja konteksti. Koko yrityksen tarvitsemat tiedot kuuluvat sen tietosivuille.',
    preferencesSaved: 'Asetukset tallennettu',
    companyInviteTitle: 'Yrityksen asetukset',
    companyInviteDescription:
      'Tilisi tarvitsee yrityskutsun. Ota yhteyttä Ponevaan yhdistääksesi tilisi.',
  },
  fr: {
    companyProfile: 'Profil de l’entreprise',
    profileUnavailable: 'Le profil de votre entreprise n’est pas disponible.',
    profileDescription:
      'Le contexte de départ pour toutes les personnes qui travaillent avec Poneva chez {company}.',
    companyName: 'Nom de l’entreprise',
    website: 'Site web',
    primaryMarket: 'Marché principal',
    companyLanguage: 'Langue de l’entreprise',
    companyMatters: 'Ce qui compte pour votre entreprise',
    profileHint: 'Un point de départ utile, sans limiter ce que vous pouvez demander.',
    reloadCompany: 'Recharger l’entreprise',
    saving: 'Enregistrement…',
    saveCompanyProfile: 'Enregistrer le profil de l’entreprise',
    profileSaved: 'Profil de l’entreprise enregistré',
    team: 'Équipe',
    teamDescription: 'Les personnes avec qui vous travaillez et ce qu’elles peuvent publier.',
    loadTeam: 'Charger l’équipe',
    people: 'Personnes',
    member: 'Membre',
    role: 'Rôle',
    companyPublishing: 'Publication de l’entreprise',
    teamPrivacy:
      'Les discussions et les brouillons restent privés jusqu’à leur partage. L’autorisation de publication ne donne jamais accès au travail privé d’une autre personne.',
    inviteColleague: 'Inviter un collègue',
    inviteIntro: 'Une invitation, une adresse e-mail vérifiée.',
    emailAddress: 'Adresse e-mail',
    invitationPlaceholder: 'collegue@entreprise.com',
    admin: 'Administrateur',
    createInvitation: 'Créer un lien d’invitation',
    invitationShare:
      'Partagez le lien généré avec votre collègue. Les invitations expirent après sept jours.',
    invitationLink: 'Lien d’invitation',
    publishingPermission: 'Autorisation de publication',
    canPublish: 'Peut publier',
    needsApproval: 'Approbation requise',
    needsApprovalDefault: 'Approbation requise · par défaut',
    canPublishDefault: 'Peut publier · par défaut',
    save: 'Enregistrer',
    savePermissions: 'Enregistrer les autorisations pour {email}',
    owner: 'Propriétaire',
    transferOwnership: 'Transférer la propriété',
    removeAccess: 'Supprimer l’accès',
    expires: 'expire',
    processing: 'Traitement par l’IA',
    processingIntro:
      'Choisissez où l’IA de Poneva s’exécute pour toutes les personnes chez {company} : discussions, voix, réunions et agents.',
    loading: 'Chargement…',
    retry: 'Réessayer',
    whatChanges: 'Ce qui change',
    currentChoice: 'Choix actuel',
    current: 'Actuel',
    keepEu: 'Effectuer tous les traitements par l’IA dans l’UE',
    useGlobal: 'Utiliser les meilleurs modèles dans le monde entier',
    euBody:
      'Pour respecter des règles strictes en matière de données. Quelques modèles et voix plus récents ne sont pas encore disponibles dans l’UE.',
    globalBody: 'Les modèles et voix les plus performants, traités dans l’UE ou ailleurs.',
    saveProcessing: 'Enregistrer',
    savingProcessing: 'Enregistrement…',
    processingSaved: 'Enregistré',
    setupAria: 'Continuez à développer votre espace de travail',
    setupMoreTitle: 'Plus de contexte. Plus de possibilités.',
    setupFirstTitle: 'Vos outils rendent cet espace plus utile.',
    setupConnected:
      'Connexions : {count}. Ajoutez un autre aspect de votre travail à la conversation.',
    setupFirstBody:
      'Connectez les outils que vous utilisez pour travailler avec vos propres informations.',
    setupAdd: 'Ajouter une autre connexion',
    setupFirst: 'Connecter votre premier outil',
    setupExplore: 'Explorer les connexions disponibles',
    setupDismiss: 'Ignorer la suggestion de connexion',
    personalMemory: 'Mémoire personnelle',
    personalMemoryDescription:
      'Les préférences et le contexte qui vous sont propres. Ce que toute l’entreprise doit savoir doit figurer sur ses pages de connaissances.',
    preferencesSaved: 'Préférences enregistrées',
    companyInviteTitle: 'Paramètres de l’entreprise',
    companyInviteDescription:
      'Votre compte nécessite une invitation à rejoindre une entreprise. Contactez Poneva pour connecter votre compte.',
  },
  de: {
    companyProfile: 'Unternehmensprofil',
    profileUnavailable: 'Ihr Unternehmensprofil ist nicht verfügbar.',
    profileDescription: 'Der Ausgangskontext für alle, die bei {company} mit Poneva arbeiten.',
    companyName: 'Unternehmensname',
    website: 'Website',
    primaryMarket: 'Hauptmarkt',
    companyLanguage: 'Unternehmenssprache',
    companyMatters: 'Was für Ihr Unternehmen wichtig ist',
    profileHint:
      'Ein hilfreicher Ausgangspunkt, keine Einschränkung dessen, was Sie fragen können.',
    reloadCompany: 'Unternehmen neu laden',
    saving: 'Wird gespeichert…',
    saveCompanyProfile: 'Unternehmensprofil speichern',
    profileSaved: 'Unternehmensprofil gespeichert',
    team: 'Team',
    teamDescription: 'Die Menschen, mit denen Sie arbeiten, und was sie veröffentlichen dürfen.',
    loadTeam: 'Team laden',
    people: 'Personen',
    member: 'Mitglied',
    role: 'Rolle',
    companyPublishing: 'Veröffentlichungen des Unternehmens',
    teamPrivacy:
      'Chats und Entwürfe bleiben privat, bis sie geteilt werden. Eine Veröffentlichungsberechtigung gewährt niemals Zugriff auf die private Arbeit anderer.',
    inviteColleague: 'Kollegin oder Kollegen einladen',
    inviteIntro: 'Eine Einladung, eine bestätigte E-Mail-Adresse.',
    emailAddress: 'E-Mail-Adresse',
    invitationPlaceholder: 'kollege@unternehmen.de',
    admin: 'Admin',
    createInvitation: 'Einladungslink erstellen',
    invitationShare:
      'Teilen Sie den erstellten Link mit Ihrer Kollegin oder Ihrem Kollegen. Einladungen laufen nach sieben Tagen ab.',
    invitationLink: 'Einladungslink',
    publishingPermission: 'Veröffentlichungsberechtigung',
    canPublish: 'Darf veröffentlichen',
    needsApproval: 'Benötigt Genehmigung',
    needsApprovalDefault: 'Benötigt Genehmigung · Standard',
    canPublishDefault: 'Darf veröffentlichen · Standard',
    save: 'Speichern',
    savePermissions: 'Berechtigungen für {email} speichern',
    owner: 'Inhaber',
    transferOwnership: 'Inhaberschaft übertragen',
    removeAccess: 'Zugriff entfernen',
    expires: 'läuft ab',
    processing: 'KI-Verarbeitung',
    processingIntro:
      'Wählen Sie, wo Ponevas KI für alle bei {company} ausgeführt wird: in Chats, bei Sprachinteraktionen, in Meetings und in Agenten.',
    loading: 'Wird geladen…',
    retry: 'Erneut versuchen',
    whatChanges: 'Was sich ändert',
    currentChoice: 'Aktuelle Auswahl',
    current: 'Aktuell',
    keepEu: 'Alle KI-Verarbeitungen in der EU durchführen',
    useGlobal: 'Die besten Modelle weltweit nutzen',
    euBody:
      'Für strenge Datenschutzvorgaben. Einige neuere Modelle und Stimmen sind in der EU noch nicht verfügbar.',
    globalBody: 'Die leistungsstärksten Modelle und Stimmen, verarbeitet in der EU oder anderswo.',
    saveProcessing: 'Speichern',
    savingProcessing: 'Wird gespeichert…',
    processingSaved: 'Gespeichert',
    setupAria: 'Arbeitsbereich weiter aufbauen',
    setupMoreTitle: 'Mehr Kontext. Mehr Möglichkeiten.',
    setupFirstTitle: 'Ihre Werkzeuge machen dies noch hilfreicher.',
    setupConnected:
      'Verbindungen: {count}. Bringen Sie einen weiteren Teil Ihrer Arbeit in die Unterhaltung ein.',
    setupFirstBody:
      'Verbinden Sie die Werkzeuge, mit denen Sie mit Ihren eigenen Informationen arbeiten.',
    setupAdd: 'Weitere Verbindung hinzufügen',
    setupFirst: 'Erstes Werkzeug verbinden',
    setupExplore: 'Verfügbare Verbindungen entdecken',
    setupDismiss: 'Verbindungsvorschlag schließen',
    personalMemory: 'Persönlicher Speicher',
    personalMemoryDescription:
      'Präferenzen und Kontext, die speziell für Sie gelten. Was das gesamte Unternehmen wissen sollte, gehört auf die Wissensseiten.',
    preferencesSaved: 'Präferenzen gespeichert',
    companyInviteTitle: 'Unternehmenseinstellungen',
    companyInviteDescription:
      'Für Ihr Konto ist eine Unternehmenseinladung erforderlich. Kontaktieren Sie Poneva, um Ihr Konto zu verbinden.',
  },
  el: {
    companyProfile: 'Προφίλ εταιρείας',
    profileUnavailable: 'Το προφίλ της εταιρείας σας δεν είναι διαθέσιμο.',
    profileDescription: 'Το αρχικό πλαίσιο για όλους όσοι εργάζονται με την Poneva στην {company}.',
    companyName: 'Όνομα εταιρείας',
    website: 'Ιστότοπος',
    primaryMarket: 'Κύρια αγορά',
    companyLanguage: 'Γλώσσα εταιρείας',
    companyMatters: 'Τι έχει σημασία για την εταιρεία σας',
    profileHint: 'Ένα χρήσιμο σημείο εκκίνησης, όχι περιορισμός σε όσα μπορείτε να ρωτήσετε.',
    reloadCompany: 'Επαναφόρτωση εταιρείας',
    saving: 'Αποθήκευση…',
    saveCompanyProfile: 'Αποθήκευση προφίλ εταιρείας',
    profileSaved: 'Το προφίλ της εταιρείας αποθηκεύτηκε',
    team: 'Ομάδα',
    teamDescription: 'Τα άτομα με τα οποία συνεργάζεστε και όσα μπορούν να δημοσιεύουν.',
    loadTeam: 'Φόρτωση ομάδας',
    people: 'Άτομα',
    member: 'Μέλος',
    role: 'Ρόλος',
    companyPublishing: 'Δημοσιεύσεις εταιρείας',
    teamPrivacy:
      'Οι συνομιλίες και τα προσχέδια παραμένουν ιδιωτικά μέχρι να κοινοποιηθούν. Η άδεια δημοσίευσης δεν παρέχει ποτέ πρόσβαση στην ιδιωτική εργασία κάποιου άλλου.',
    inviteColleague: 'Πρόσκληση συνεργάτη',
    inviteIntro: 'Μία πρόσκληση, μία επαληθευμένη διεύθυνση email.',
    emailAddress: 'Διεύθυνση email',
    invitationPlaceholder: 'colleague@company.com',
    admin: 'Διαχειριστής',
    createInvitation: 'Δημιουργία συνδέσμου πρόσκλησης',
    invitationShare:
      'Μοιραστείτε τον σύνδεσμο που δημιουργήθηκε με τον συνεργάτη σας. Οι προσκλήσεις λήγουν μετά από επτά ημέρες.',
    invitationLink: 'Σύνδεσμος πρόσκλησης',
    publishingPermission: 'Άδεια δημοσίευσης',
    canPublish: 'Μπορεί να δημοσιεύει',
    needsApproval: 'Απαιτείται έγκριση',
    needsApprovalDefault: 'Απαιτείται έγκριση · προεπιλογή',
    canPublishDefault: 'Μπορεί να δημοσιεύει · προεπιλογή',
    save: 'Αποθήκευση',
    savePermissions: 'Αποθήκευση δικαιωμάτων για {email}',
    owner: 'Ιδιοκτήτης',
    transferOwnership: 'Μεταβίβαση ιδιοκτησίας',
    removeAccess: 'Αφαίρεση πρόσβασης',
    expires: 'λήγει',
    processing: 'Επεξεργασία από AI',
    processingIntro:
      'Επιλέξτε πού θα εκτελείται η τεχνητή νοημοσύνη της Poneva για όλους στην {company}: συνομιλίες, φωνή, συσκέψεις και πράκτορες.',
    loading: 'Φόρτωση…',
    retry: 'Δοκιμάστε ξανά',
    whatChanges: 'Τι αλλάζει',
    currentChoice: 'Τρέχουσα επιλογή',
    current: 'Τρέχον',
    keepEu: 'Διατήρηση όλης της επεξεργασίας AI στην ΕΕ',
    useGlobal: 'Χρήση των καλύτερων μοντέλων παγκοσμίως',
    euBody:
      'Για αυστηρούς κανόνες δεδομένων. Ορισμένα νεότερα μοντέλα και φωνές δεν είναι ακόμη διαθέσιμα στην ΕΕ.',
    globalBody: 'Τα ισχυρότερα μοντέλα και οι φωνές, με επεξεργασία στην ΕΕ ή αλλού.',
    saveProcessing: 'Αποθήκευση',
    savingProcessing: 'Αποθήκευση…',
    processingSaved: 'Αποθηκεύτηκε',
    setupAria: 'Συνεχίστε να διαμορφώνετε τον χώρο εργασίας σας',
    setupMoreTitle: 'Περισσότερο πλαίσιο. Περισσότερες δυνατότητες.',
    setupFirstTitle: 'Τα εργαλεία σας κάνουν την εργασία πιο χρήσιμη.',
    setupConnected: 'Συνδέσεις: {count}. Φέρτε ένα ακόμη μέρος της εργασίας σας στη συνομιλία.',
    setupFirstBody:
      'Συνδέστε τα εργαλεία που χρησιμοποιείτε για να εργάζεστε με τις δικές σας πληροφορίες.',
    setupAdd: 'Προσθήκη ακόμη μίας σύνδεσης',
    setupFirst: 'Συνδέστε το πρώτο σας εργαλείο',
    setupExplore: 'Εξερεύνηση διαθέσιμων συνδέσεων',
    setupDismiss: 'Απόρριψη πρότασης σύνδεσης',
    personalMemory: 'Προσωπική μνήμη',
    personalMemoryDescription:
      'Προτιμήσεις και πλαίσιο που αφορούν εσάς. Όσα πρέπει να γνωρίζει ολόκληρη η εταιρεία ανήκουν στις σελίδες Γνώσης της.',
    preferencesSaved: 'Οι προτιμήσεις αποθηκεύτηκαν',
    companyInviteTitle: 'Ρυθμίσεις εταιρείας',
    companyInviteDescription:
      'Ο λογαριασμός σας χρειάζεται πρόσκληση από εταιρεία. Επικοινωνήστε με την Poneva για να συνδέσετε τον λογαριασμό σας.',
  },
  hu: {
    companyProfile: 'Cégprofil',
    profileUnavailable: 'A vállalati profilja nem érhető el.',
    profileDescription:
      'A Poneva használatának kiinduló kontextusa mindenki számára a(z) {company} vállalatnál.',
    companyName: 'Vállalat neve',
    website: 'Webhely',
    primaryMarket: 'Elsődleges piac',
    companyLanguage: 'Vállalat nyelve',
    companyMatters: 'Mi fontos a vállalatának',
    profileHint: 'Hasznos kiindulópont, nem korlátozza, mit kérdezhet.',
    reloadCompany: 'Cégprofil újratöltése',
    saving: 'Mentés…',
    saveCompanyProfile: 'Vállalati profil mentése',
    profileSaved: 'Vállalati profil mentve',
    team: 'Csapat',
    teamDescription: 'Azok, akikkel együtt dolgozik, és amit közzétehetnek.',
    loadTeam: 'Csapat betöltése',
    people: 'Személyek',
    member: 'Tag',
    role: 'Szerepkör',
    companyPublishing: 'Vállalati közzététel',
    teamPrivacy:
      'A csevegések és piszkozatok megosztásig privátak maradnak. A közzétételi jogosultság soha nem biztosít hozzáférést más privát munkájához.',
    inviteColleague: 'Munkatárs meghívása',
    inviteIntro: 'Egy meghívó, egy ellenőrzött e-mail-cím.',
    emailAddress: 'E-mail-cím',
    invitationPlaceholder: 'kollega@ceg.hu',
    admin: 'Adminisztrátor',
    createInvitation: 'Meghívólink létrehozása',
    invitationShare:
      'Ossza meg a létrehozott linket munkatársával. A meghívók hét nap után lejárnak.',
    invitationLink: 'Meghívólink',
    publishingPermission: 'Közzétételi jogosultság',
    canPublish: 'Közzétehet',
    needsApproval: 'Jóváhagyás szükséges',
    needsApprovalDefault: 'Jóváhagyás szükséges · alapértelmezett',
    canPublishDefault: 'Közzétehet · alapértelmezett',
    save: 'Mentés',
    savePermissions: 'Jogosultságok mentése a(z) {email} számára',
    owner: 'Tulajdonos',
    transferOwnership: 'Tulajdonjog átruházása',
    removeAccess: 'Hozzáférés eltávolítása',
    expires: 'lejár',
    processing: 'AI-feldolgozás',
    processingIntro:
      'Válassza ki, hol fusson a Poneva AI-ja mindenki számára a(z) {company} vállalatnál: csevegésekben, hangalapú használatkor, megbeszéléseken és ügynököknél.',
    loading: 'Betöltés…',
    retry: 'Próbálja újra',
    whatChanges: 'Mi változik',
    currentChoice: 'Jelenlegi választás',
    current: 'Jelenlegi',
    keepEu: 'Minden AI-feldolgozás az EU-ban maradjon',
    useGlobal: 'A világ legjobb modelljeinek használata',
    euBody:
      'Szigorú adatvédelmi szabályokhoz. Néhány újabb modell és hang még nem érhető el az EU-ban.',
    globalBody: 'A legerősebb modellek és hangok használata, az EU-ban vagy máshol feldolgozva.',
    saveProcessing: 'Mentés',
    savingProcessing: 'Mentés…',
    processingSaved: 'Mentve',
    setupAria: 'Munkaterület további beállítása',
    setupMoreTitle: 'Több kontextus. Több lehetőség.',
    setupFirstTitle: 'Eszközei még hasznosabbá teszik.',
    setupConnected: 'Kapcsolatok: {count}. Hozza be munkája egy másik részét is a beszélgetésbe.',
    setupFirstBody:
      'Csatlakoztassa a munkájához használt eszközöket, hogy saját információival dolgozhasson.',
    setupAdd: 'Újabb kapcsolat hozzáadása',
    setupFirst: 'Első eszköz csatlakoztatása',
    setupExplore: 'Elérhető kapcsolatok felfedezése',
    setupDismiss: 'Kapcsolódási javaslat elvetése',
    personalMemory: 'Személyes memória',
    personalMemoryDescription:
      'Az Önre jellemző beállítások és kontextus. Amit az egész vállalatnak tudnia kell, annak a Tudás oldalain a helye.',
    preferencesSaved: 'Beállítások mentve',
    companyInviteTitle: 'Vállalati beállítások',
    companyInviteDescription:
      'Fiókjához vállalati meghívó szükséges. Fiókja csatlakoztatásához lépjen kapcsolatba a Ponevával.',
  },
  ga: {
    companyProfile: 'Próifíl na cuideachta',
    profileUnavailable: 'Níl próifíl do chuideachta ar fáil.',
    profileDescription: 'An comhthéacs tosaigh do gach duine a oibríonn le Poneva ag {company}.',
    companyName: 'Ainm na cuideachta',
    website: 'Suíomh gréasáin',
    primaryMarket: 'Príomhmhargadh',
    companyLanguage: 'Teanga na cuideachta',
    companyMatters: 'Cad atá tábhachtach do do chuideachta',
    profileHint: 'Túsphointe úsáideach, ní srian ar na rudaí is féidir leat a iarraidh.',
    reloadCompany: 'Athlódáil an chuideachta',
    saveCompanyProfile: 'Sábháil próifíl na cuideachta',
    profileSaved: 'Sábháladh próifíl na cuideachta',
    team: 'Foireann',
    teamDescription: 'Na daoine a n-oibríonn tú leo, agus na rudaí is féidir leo a fhoilsiú.',
    loadTeam: 'Luchtaigh an fhoireann',
    people: 'Daoine',
    member: 'Ball',
    role: 'Ról',
    companyPublishing: 'Foilsitheoireacht na cuideachta',
    teamPrivacy:
      'Fanann comhráite agus dréachtaí príobháideach go dtí go roinntear iad. Ní thugann cead foilsitheoireachta rochtain ar obair phríobháideach duine eile riamh.',
    inviteColleague: 'Tabhair cuireadh do chomhghleacaí',
    inviteIntro: 'Cuireadh amháin, seoladh ríomhphoist fíoraithe amháin.',
    emailAddress: 'Seoladh ríomhphoist',
    invitationPlaceholder: 'colleague@company.com',
    admin: 'Riarthóir',
    createInvitation: 'Cruthaigh nasc cuireadh',
    invitationShare:
      'Roinn an nasc ginte le do chomhghleacaí. Rachaidh cuirí in éag tar éis seacht lá.',
    invitationLink: 'Nasc cuireadh',
    publishingPermission: 'Cead foilsitheoireachta',
    canPublish: 'Is féidir foilsiú',
    needsApproval: 'Teastaíonn ceadú',
    needsApprovalDefault: 'Teastaíonn ceadú · réamhshocrú',
    canPublishDefault: 'Is féidir foilsiú · réamhshocrú',
    save: 'Sábháil',
    savePermissions: 'Sábháil ceadanna do {email}',
    owner: 'Úinéir',
    transferOwnership: 'Aistrigh úinéireacht',
    removeAccess: 'Bain rochtain',
    expires: 'rachaidh in éag',
    processing: 'Próiseáil AI',
    processingIntro:
      'Roghnaigh cá ritheann AI Poneva do gach duine ag {company}: comhrá, guth, cruinnithe agus gníomhairí.',
    loading: 'Á lódáil…',
    retry: 'Bain triail eile as',
    whatChanges: 'Cad a athraíonn',
    currentChoice: 'An rogha reatha',
    current: 'Reatha',
    keepEu: 'Coinnigh próiseáil uile AI san AE',
    useGlobal: 'Úsáid na samhlacha is fearr ar fud an domhain',
    euBody:
      'Le haghaidh rialacha dochta sonraí. Níl cúpla samhail agus guth níos nuaí ar fáil san AE go fóill.',
    globalBody: 'Na samhlacha agus na guthanna is láidre, próiseáilte san AE nó in áiteanna eile.',
    saveProcessing: 'Sábháil',
    savingProcessing: 'Á shábháil…',
    processingSaved: 'Sábháilte',
    setupAria: 'Lean ort ag cur le do spás oibre',
    setupMoreTitle: 'Tuilleadh comhthéacs. Tuilleadh féidearthachtaí.',
    setupFirstTitle: 'Cuireann d’uirlisí níos mó úsáide leis seo.',
    setupConnected: 'Naisc: {count}. Tabhair cuid eile de do chuid oibre isteach sa chomhrá.',
    setupFirstBody: 'Nasc na huirlisí a úsáideann tú chun oibriú le do chuid faisnéise féin.',
    setupAdd: 'Cuir nasc eile leis',
    setupFirst: 'Nasc do chéad uirlis',
    setupExplore: 'Brabhsáil naisc atá ar fáil',
    setupDismiss: 'Dún moladh an naisc',
    personalMemory: 'Cuimhne phearsanta',
    personalMemoryDescription:
      'Sainroghanna agus comhthéacs a bhaineann leat féin. Baineann an méid ba cheart don chuideachta ar fad a bheith ar an eolas faoi le leathanaigh Eolais na cuideachta.',
    preferencesSaved: 'Sábháladh na sainroghanna',
    companyInviteTitle: 'Socruithe na cuideachta',
    companyInviteDescription:
      'Teastaíonn cuireadh cuideachta ó do chuntas. Déan teagmháil le Poneva chun do chuntas a nascadh.',
    saving: 'Á shábháil…',
  },
  it: {
    companyProfile: 'Profilo aziendale',
    profileUnavailable: 'Il profilo della tua azienda non è disponibile.',
    profileDescription: 'Il contesto iniziale per chiunque lavori con Poneva presso {company}.',
    companyName: 'Nome dell’azienda',
    website: 'Sito web',
    primaryMarket: 'Mercato principale',
    companyLanguage: 'Lingua aziendale',
    companyMatters: 'Cosa conta per la tua azienda',
    profileHint: 'Un punto di partenza utile, non una limitazione a ciò che puoi chiedere.',
    reloadCompany: 'Ricarica azienda',
    saving: 'Salvataggio…',
    saveCompanyProfile: 'Salva profilo aziendale',
    profileSaved: 'Profilo aziendale salvato',
    team: 'Team',
    teamDescription: 'Le persone con cui lavori e ciò che possono pubblicare.',
    loadTeam: 'Carica team',
    people: 'Persone',
    member: 'Membro',
    role: 'Ruolo',
    companyPublishing: 'Pubblicazione aziendale',
    teamPrivacy:
      'Le chat e le bozze restano private finché non vengono condivise. Il permesso di pubblicazione non dà mai accesso al lavoro privato di altre persone.',
    inviteColleague: 'Invita un collega',
    inviteIntro: 'Un invito, un indirizzo email verificato.',
    emailAddress: 'Indirizzo email',
    invitationPlaceholder: 'collega@azienda.com',
    admin: 'Amministratore',
    createInvitation: 'Crea link d’invito',
    invitationShare:
      'Condividi il link generato con il tuo collega. Gli inviti scadono dopo sette giorni.',
    invitationLink: 'Link d’invito',
    publishingPermission: 'Permesso di pubblicazione',
    canPublish: 'Può pubblicare',
    needsApproval: 'Richiede approvazione',
    needsApprovalDefault: 'Richiede approvazione · predefinito',
    canPublishDefault: 'Può pubblicare · predefinito',
    save: 'Salva',
    savePermissions: 'Salva i permessi per {email}',
    owner: 'Proprietario',
    transferOwnership: 'Trasferisci proprietà',
    removeAccess: 'Rimuovi accesso',
    expires: 'scade',
    processing: 'Elaborazione IA',
    processingIntro:
      'Scegli dove viene eseguita l’IA di Poneva per tutti presso {company}: chat, voce, riunioni e agenti.',
    loading: 'Caricamento…',
    retry: 'Riprova',
    whatChanges: 'Cosa cambia',
    currentChoice: 'Scelta attuale',
    current: 'Attuale',
    keepEu: 'Mantieni tutta l’elaborazione IA nell’UE',
    useGlobal: 'Usa i migliori modelli disponibili in tutto il mondo',
    euBody:
      'Per requisiti rigorosi in materia di dati. Alcuni modelli e voci più recenti non sono ancora disponibili nell’UE.',
    globalBody: 'I modelli e le voci più avanzati, elaborati nell’UE o altrove.',
    saveProcessing: 'Salva',
    savingProcessing: 'Salvataggio…',
    processingSaved: 'Salvato',
    setupAria: 'Continua a configurare il tuo workspace',
    setupMoreTitle: 'Più contesto. Più possibilità.',
    setupFirstTitle: 'I tuoi strumenti rendono tutto più utile.',
    setupConnected:
      "Connessioni: {count}. Porta un'altra parte del tuo lavoro nella conversazione.",
    setupFirstBody: 'Collega gli strumenti che usi per lavorare con le tue informazioni.',
    setupAdd: 'Aggiungi un’altra connessione',
    setupFirst: 'Collega il tuo primo strumento',
    setupExplore: 'Esplora le connessioni disponibili',
    setupDismiss: 'Ignora il suggerimento di connessione',
    personalMemory: 'Memoria personale',
    personalMemoryDescription:
      'Preferenze e contesto specifici per te. Ciò che dovrebbe sapere l’intera azienda appartiene alle sue pagine Knowledge.',
    preferencesSaved: 'Preferenze salvate',
    companyInviteTitle: 'Impostazioni aziendali',
    companyInviteDescription:
      'Il tuo account richiede un invito aziendale. Contatta Poneva per collegare il tuo account.',
  },
  lv: {
    companyProfile: 'Uzņēmuma profils',
    profileUnavailable: 'Jūsu uzņēmuma profils nav pieejams.',
    profileDescription: 'Sākotnējais konteksts ikvienam, kas uzņēmumā {company} strādā ar Poneva.',
    companyName: 'Uzņēmuma nosaukums',
    website: 'Tīmekļa vietne',
    primaryMarket: 'Galvenais tirgus',
    companyLanguage: 'Uzņēmuma valoda',
    companyMatters: 'Kas ir svarīgi jūsu uzņēmumam',
    profileHint: 'Noderīgs sākumpunkts, nevis ierobežojums tam, ko varat jautāt.',
    reloadCompany: 'Atkārtoti ielādēt uzņēmumu',
    saving: 'Saglabā…',
    saveCompanyProfile: 'Saglabāt uzņēmuma profilu',
    profileSaved: 'Uzņēmuma profils saglabāts',
    team: 'Komanda',
    teamDescription: 'Cilvēki, ar kuriem strādājat, un tas, ko viņi var publicēt.',
    loadTeam: 'Ielādēt komandu',
    people: 'Cilvēki',
    member: 'Dalībnieks',
    role: 'Loma',
    companyPublishing: 'Uzņēmuma publicēšana',
    teamPrivacy:
      'Sarunas un melnraksti paliek privāti, līdz tie tiek kopīgoti. Publicēšanas atļauja nekad nepiešķir piekļuvi cita lietotāja privātajam darbam.',
    inviteColleague: 'Uzaicināt kolēģi',
    inviteIntro: 'Viens uzaicinājums, viena verificēta e-pasta adrese.',
    emailAddress: 'E-pasta adrese',
    invitationPlaceholder: 'colleague@company.com',
    admin: 'Administrators',
    createInvitation: 'Izveidot uzaicinājuma saiti',
    invitationShare:
      'Kopīgojiet izveidoto saiti ar kolēģi. Uzaicinājumi zaudē spēku pēc septiņām dienām.',
    invitationLink: 'Uzaicinājuma saite',
    publishingPermission: 'Publicēšanas atļauja',
    canPublish: 'Var publicēt',
    needsApproval: 'Nepieciešams apstiprinājums',
    needsApprovalDefault: 'Nepieciešams apstiprinājums · noklusējums',
    canPublishDefault: 'Var publicēt · noklusējums',
    save: 'Saglabāt',
    savePermissions: 'Saglabāt {email} atļaujas',
    owner: 'Īpašnieks',
    transferOwnership: 'Nodot īpašumtiesības',
    removeAccess: 'Noņemt piekļuvi',
    expires: 'beidzas',
    processing: 'MI apstrāde',
    processingIntro:
      'Izvēlieties, kur visiem uzņēmumā {company} darbojas Poneva MI: tērzēšanā, balss saziņā, sapulcēs un aģentos.',
    loading: 'Ielādē…',
    retry: 'Mēģināt vēlreiz',
    whatChanges: 'Kas mainās',
    currentChoice: 'Pašreizējā izvēle',
    current: 'Pašreizējais',
    keepEu: 'Saglabāt visu MI apstrādi ES',
    useGlobal: 'Izmantot labākos modeļus visā pasaulē',
    euBody:
      'Stingru datu noteikumu ievērošanai. Daži jaunāki modeļi un balsis ES vēl nav pieejami.',
    globalBody: 'Jaudīgākie modeļi un balsis, kas tiek apstrādāti ES vai citur.',
    saveProcessing: 'Saglabāt',
    savingProcessing: 'Saglabā…',
    processingSaved: 'Saglabāts',
    setupAria: 'Turpiniet veidot savu darbvietu',
    setupMoreTitle: 'Vairāk konteksta. Vairāk iespēju.',
    setupFirstTitle: 'Jūsu rīki padara šo vēl noderīgāku.',
    setupConnected: 'Savienojumi: {count}. Iekļaujiet sarunā vēl vienu sava darba daļu.',
    setupFirstBody: 'Savienojiet rīkus, ko izmantojat darbā ar savu informāciju.',
    setupAdd: 'Pievienot vēl vienu savienojumu',
    setupFirst: 'Savienot savu pirmo rīku',
    setupExplore: 'Izpētīt pieejamos savienojumus',
    setupDismiss: 'Noraidīt savienojuma ieteikumu',
    personalMemory: 'Personīgā atmiņa',
    personalMemoryDescription:
      'Tikai jums paredzētas preferences un konteksts. Tas, kas jāzina visam uzņēmumam, pieder tā Zināšanu lapām.',
    preferencesSaved: 'Preferences saglabātas',
    companyInviteTitle: 'Uzņēmuma iestatījumi',
    companyInviteDescription:
      'Jūsu kontam ir nepieciešams uzņēmuma uzaicinājums. Sazinieties ar Poneva, lai savienotu savu kontu.',
  },
  lt: {
    companyProfile: 'Įmonės profilis',
    profileUnavailable: 'Jūsų įmonės profilis nepasiekiamas.',
    profileDescription: 'Pradinis kontekstas visiems, dirbantiems su Poneva įmonėje {company}.',
    companyName: 'Įmonės pavadinimas',
    website: 'Svetainė',
    primaryMarket: 'Pagrindinė rinka',
    companyLanguage: 'Įmonės kalba',
    companyMatters: 'Kas svarbu jūsų įmonei',
    profileHint: 'Naudingas pradinis kontekstas, o ne apribojimas, ko galite klausti.',
    reloadCompany: 'Iš naujo įkelti įmonę',
    saving: 'Išsaugoma…',
    saveCompanyProfile: 'Išsaugoti įmonės profilį',
    profileSaved: 'Įmonės profilis išsaugotas',
    team: 'Komanda',
    teamDescription: 'Žmonės, su kuriais dirbate, ir ką jie gali skelbti.',
    loadTeam: 'Įkelti komandą',
    people: 'Žmonės',
    member: 'Narys',
    role: 'Vaidmuo',
    companyPublishing: 'Skelbimas įmonės vardu',
    teamPrivacy:
      'Pokalbiai ir juodraščiai lieka privatūs, kol jais nepasidalijama. Skelbimo teisė niekada nesuteikia prieigos prie kito asmens privataus darbo.',
    inviteColleague: 'Pakviesti kolegą',
    inviteIntro: 'Vienas kvietimas, vienas patvirtintas el. pašto adresas.',
    emailAddress: 'El. pašto adresas',
    invitationPlaceholder: 'colleague@company.com',
    admin: 'Administratorius',
    createInvitation: 'Sukurti kvietimo nuorodą',
    invitationShare:
      'Pasidalykite sugeneruota nuoroda su kolega. Kvietimai galioja septynias dienas.',
    invitationLink: 'Kvietimo nuoroda',
    publishingPermission: 'Skelbimo teisė',
    canPublish: 'Gali skelbti',
    needsApproval: 'Reikia patvirtinimo',
    needsApprovalDefault: 'Reikia patvirtinimo · numatyta',
    canPublishDefault: 'Gali skelbti · numatyta',
    save: 'Išsaugoti',
    savePermissions: 'Išsaugoti {email} teises',
    owner: 'Savininkas',
    transferOwnership: 'Perleisti nuosavybę',
    removeAccess: 'Pašalinti prieigą',
    expires: 'baigia galioti',
    processing: 'DI apdorojimas',
    processingIntro:
      'Pasirinkite, kur visiems, dirbantiems {company}, veiks Poneva DI: pokalbiuose, balsu, susitikimuose ir agentuose.',
    loading: 'Įkeliama…',
    retry: 'Bandyti dar kartą',
    whatChanges: 'Kas pasikeis',
    currentChoice: 'Dabartinis pasirinkimas',
    current: 'Dabartinis',
    keepEu: 'Visą DI apdorojimą vykdyti ES',
    useGlobal: 'Naudoti geriausius modelius visame pasaulyje',
    euBody:
      'Kai taikomos griežtos duomenų taisyklės. Kai kurie naujesni modeliai ir balsai ES dar nepasiekiami.',
    globalBody: 'Galingiausi modeliai ir balsai apdorojami ES arba kitur.',
    saveProcessing: 'Išsaugoti',
    savingProcessing: 'Išsaugoma…',
    processingSaved: 'Išsaugota',
    setupAria: 'Tęskite savo darbo aplinkos kūrimą',
    setupMoreTitle: 'Daugiau konteksto. Daugiau galimybių.',
    setupFirstTitle: 'Jūsų įrankiai padeda iš to gauti daugiau naudos.',
    setupConnected: 'Ryšiai: {count}. Įtraukite į pokalbį dar vieną savo darbo dalį.',
    setupFirstBody: 'Prijunkite įrankius, kuriuos naudojate dirbdami su savo informacija.',
    setupAdd: 'Pridėti dar vieną integraciją',
    setupFirst: 'Prijungti pirmąjį įrankį',
    setupExplore: 'Peržiūrėti galimas integracijas',
    setupDismiss: 'Atmesti integracijos pasiūlymą',
    personalMemory: 'Asmeninė atmintis',
    personalMemoryDescription:
      'Jums būdingos nuostatos ir kontekstas. Tai, ką turėtų žinoti visa įmonė, priklauso jos Žinių puslapiams.',
    preferencesSaved: 'Nuostatos išsaugotos',
    companyInviteTitle: 'Įmonės nustatymai',
    companyInviteDescription:
      'Jūsų paskyrai reikia įmonės kvietimo. Susisiekite su Poneva, kad prijungtumėte savo paskyrą.',
  },
  mt: {
    companyProfile: 'Profil tal-kumpanija',
    profileUnavailable: 'Il-profil tal-kumpanija tiegħek mhuwiex disponibbli.',
    profileDescription: "Il-kuntest inizjali għal kull min jaħdem ma' Poneva f'{company}.",
    companyName: 'Isem il-kumpanija',
    website: 'Sit web',
    primaryMarket: 'Suq ewlieni',
    companyLanguage: 'Lingwa tal-kumpanija',
    companyMatters: "X'inhu importanti għall-kumpanija tiegħek",
    profileHint: "Punt tat-tluq utli, mhux restrizzjoni fuq dak li tista' tistaqsi.",
    reloadCompany: "Erġa' tella' l-kumpanija",
    saving: 'Qed jiġi ssejvjat…',
    saveCompanyProfile: 'Issejvja l-profil tal-kumpanija',
    profileSaved: 'Il-profil tal-kumpanija ġie ssejvjat',
    team: 'Tim',
    teamDescription: 'In-nies li taħdem magħhom, u dak li jistgħu jippubblikaw.',
    loadTeam: "Tella' t-tim",
    people: 'Nies',
    member: 'Membru',
    role: 'Rwol',
    companyPublishing: 'Pubblikazzjoni tal-kumpanija',
    teamPrivacy:
      "Iċ-chats u l-abbozzi jibqgħu privati sakemm jinqasmu. Il-permess tal-pubblikazzjoni qatt ma jagħti aċċess għax-xogħol privat ta' ħaddieħor.",
    inviteColleague: 'Stieden kollega',
    inviteIntro: 'Stedina waħda, indirizz tal-email ivverifikat wieħed.',
    emailAddress: 'Indirizz tal-email',
    invitationPlaceholder: 'kollega@kumpanija.com',
    admin: 'Amministratur',
    createInvitation: 'Oħloq link għall-istedina',
    invitationShare:
      'Aqsam il-link iġġenerat mal-kollega tiegħek. L-istediniet jiskadu wara sebat ijiem.',
    invitationLink: 'Link għall-istedina',
    publishingPermission: 'Permess tal-pubblikazzjoni',
    canPublish: "Jista' jippubblika",
    needsApproval: 'Jeħtieġ approvazzjoni',
    needsApprovalDefault: 'Jeħtieġ approvazzjoni · default',
    canPublishDefault: "Jista' jippubblika · default",
    save: 'Issejvja',
    savePermissions: 'Issejvja l-permessi għal {email}',
    owner: 'Sid',
    transferOwnership: 'Ittrasferixxi s-sjieda',
    removeAccess: 'Neħħi l-aċċess',
    expires: 'jiskadi',
    processing: 'Ipproċessar tal-AI',
    processingIntro:
      "Agħżel fejn taħdem l-AI ta' Poneva għal kulħadd f'{company}: chat, vuċi, laqgħat u aġenti.",
    loading: 'Qed jitgħabba…',
    retry: "Erġa' pprova",
    whatChanges: "X'jinbidel",
    currentChoice: 'Għażla attwali',
    current: 'Attwali',
    keepEu: 'Żomm l-ipproċessar kollu tal-AI fl-UE',
    useGlobal: 'Uża l-aħjar mudelli madwar id-dinja',
    euBody:
      'Għal regoli stretti dwar id-dejta. Xi mudelli u vuċijiet ġodda għadhom mhumiex disponibbli fl-UE.',
    globalBody: "L-aktar mudelli u vuċijiet b'saħħithom, ipproċessati fl-UE jew x'imkien ieħor.",
    saveProcessing: 'Issejvja',
    savingProcessing: 'Qed jiġi ssejvjat…',
    processingSaved: 'Ġie ssejvjat',
    setupAria: 'Kompli ħejji l-ispazju tax-xogħol tiegħek',
    setupMoreTitle: 'Aktar kuntest. Aktar possibbiltajiet.',
    setupFirstTitle: 'L-għodod tiegħek jagħmlu dan aktar utli.',
    setupConnected:
      'Konnessjonijiet: {count}. Ġib parti oħra mix-xogħol tiegħek fil-konversazzjoni.',
    setupFirstBody: 'Qabbad l-għodod li tuża biex taħdem bl-informazzjoni tiegħek stess.',
    setupAdd: 'Żid konnessjoni oħra',
    setupFirst: 'Qabbad l-ewwel għodda tiegħek',
    setupExplore: 'Esplora l-konnessjonijiet disponibbli',
    setupDismiss: 'Neħħi s-suġġeriment tal-konnessjoni',
    personalMemory: 'Memorja personali',
    personalMemoryDescription:
      'Preferenzi u kuntest speċifiċi għalik. Dak li għandha tkun taf il-kumpanija kollha jappartjeni għall-paġni tal-Għarfien tagħha.',
    preferencesSaved: 'Il-preferenzi ġew issejvjati',
    companyInviteTitle: 'Settings tal-kumpanija',
    companyInviteDescription:
      'Il-kont tiegħek jeħtieġ stedina mill-kumpanija. Ikkuntattja lil Poneva biex tikkonnettja l-kont tiegħek.',
  },
  pl: {
    companyProfile: 'Profil firmy',
    profileUnavailable: 'Profil Twojej firmy jest niedostępny.',
    profileDescription: 'Punkt wyjścia dla wszystkich, którzy pracują z Poneva w firmie {company}.',
    companyName: 'Nazwa firmy',
    website: 'Strona internetowa',
    primaryMarket: 'Główny rynek',
    companyLanguage: 'Język firmy',
    companyMatters: 'Co jest ważne dla Twojej firmy',
    profileHint: 'Przydatny punkt wyjścia, a nie ograniczenie tego, o co możesz pytać.',
    reloadCompany: 'Odśwież firmę',
    saving: 'Zapisywanie…',
    saveCompanyProfile: 'Zapisz profil firmy',
    profileSaved: 'Profil firmy zapisany',
    team: 'Zespół',
    teamDescription: 'Osoby, z którymi pracujesz, oraz to, co mogą publikować.',
    loadTeam: 'Wczytaj zespół',
    people: 'Osoby',
    member: 'Członek',
    role: 'Rola',
    companyPublishing: 'Publikowanie w firmie',
    teamPrivacy:
      'Czaty i wersje robocze pozostają prywatne do czasu ich udostępnienia. Uprawnienie do publikowania nigdy nie daje dostępu do prywatnych materiałów innej osoby.',
    inviteColleague: 'Zaproś współpracownika',
    inviteIntro: 'Jedno zaproszenie, jeden zweryfikowany adres e-mail.',
    emailAddress: 'Adres e-mail',
    invitationPlaceholder: 'wspolpracownik@firma.pl',
    admin: 'Administrator',
    createInvitation: 'Utwórz link zaproszenia',
    invitationShare:
      'Udostępnij wygenerowany link współpracownikowi. Zaproszenia wygasają po siedmiu dniach.',
    invitationLink: 'Link zaproszenia',
    publishingPermission: 'Uprawnienie do publikowania',
    canPublish: 'Może publikować',
    needsApproval: 'Wymaga zatwierdzenia',
    needsApprovalDefault: 'Wymaga zatwierdzenia · domyślnie',
    canPublishDefault: 'Może publikować · domyślnie',
    save: 'Zapisz',
    savePermissions: 'Zapisz uprawnienia dla {email}',
    owner: 'Właściciel',
    transferOwnership: 'Przekaż własność',
    removeAccess: 'Usuń dostęp',
    expires: 'wygasa',
    processing: 'Przetwarzanie AI',
    processingIntro:
      'Wybierz, gdzie działa AI Poneva dla wszystkich w firmie {company}: na czacie, w głosie, podczas spotkań i w agentach.',
    loading: 'Wczytywanie…',
    retry: 'Spróbuj ponownie',
    whatChanges: 'Co się zmieni',
    currentChoice: 'Bieżący wybór',
    current: 'Bieżące',
    keepEu: 'Zachowaj całe przetwarzanie AI w UE',
    useGlobal: 'Używaj najlepszych modeli na całym świecie',
    euBody:
      'Dla rygorystycznych zasad dotyczących danych. Kilka nowszych modeli i głosów nie jest jeszcze dostępnych w UE.',
    globalBody: 'Najlepsze modele i głosy, przetwarzane w UE lub gdzie indziej.',
    saveProcessing: 'Zapisz',
    savingProcessing: 'Zapisywanie…',
    processingSaved: 'Zapisano',
    setupAria: 'Kontynuuj tworzenie obszaru roboczego',
    setupMoreTitle: 'Więcej kontekstu. Więcej możliwości.',
    setupFirstTitle: 'Twoje narzędzia pozwalają zrobić więcej.',
    setupConnected: 'Połączenia: {count}. Dodaj do rozmowy kolejną część swojej pracy.',
    setupFirstBody: 'Połącz narzędzia, których używasz, aby pracować z własnymi informacjami.',
    setupAdd: 'Dodaj kolejne połączenie',
    setupFirst: 'Połącz pierwsze narzędzie',
    setupExplore: 'Przeglądaj dostępne połączenia',
    setupDismiss: 'Odrzuć sugestię połączenia',
    personalMemory: 'Pamięć osobista',
    personalMemoryDescription:
      'Preferencje i kontekst dotyczące Ciebie. To, co powinna wiedzieć cała firma, należy umieszczać na stronach Bazy wiedzy.',
    preferencesSaved: 'Preferencje zapisane',
    companyInviteTitle: 'Ustawienia firmy',
    companyInviteDescription:
      'Twoje konto wymaga zaproszenia do firmy. Skontaktuj się z Poneva, aby połączyć swoje konto.',
  },
  pt: {
    companyProfile: 'Perfil da empresa',
    profileUnavailable: 'O perfil da sua empresa não está disponível.',
    profileDescription:
      'O contexto inicial para todas as pessoas que trabalham com a Poneva na {company}.',
    companyName: 'Nome da empresa',
    website: 'Site',
    primaryMarket: 'Mercado principal',
    companyLanguage: 'Idioma da empresa',
    companyMatters: 'O que é importante para sua empresa',
    profileHint: 'Um ponto de partida útil, não uma restrição ao que você pode perguntar.',
    reloadCompany: 'Recarregar empresa',
    saving: 'Salvando…',
    saveCompanyProfile: 'Salvar perfil da empresa',
    profileSaved: 'Perfil da empresa salvo',
    team: 'Equipe',
    teamDescription: 'As pessoas com quem você trabalha e o que elas podem publicar.',
    loadTeam: 'Carregar equipe',
    people: 'Pessoas',
    member: 'Membro',
    role: 'Função',
    companyPublishing: 'Publicação da empresa',
    teamPrivacy:
      'Os chats e rascunhos permanecem privados até serem compartilhados. A permissão de publicação nunca concede acesso ao trabalho privado de outra pessoa.',
    inviteColleague: 'Convidar uma pessoa colega',
    inviteIntro: 'Um convite, um endereço de e-mail verificado.',
    emailAddress: 'Endereço de e-mail',
    invitationPlaceholder: 'colega@empresa.com',
    admin: 'Administrador',
    createInvitation: 'Criar link de convite',
    invitationShare:
      'Compartilhe o link gerado com sua pessoa colega. Os convites expiram após sete dias.',
    invitationLink: 'Link de convite',
    publishingPermission: 'Permissão de publicação',
    canPublish: 'Pode publicar',
    needsApproval: 'Precisa de aprovação',
    needsApprovalDefault: 'Precisa de aprovação · padrão',
    canPublishDefault: 'Pode publicar · padrão',
    save: 'Salvar',
    savePermissions: 'Salvar permissões para {email}',
    owner: 'Proprietário',
    transferOwnership: 'Transferir propriedade',
    removeAccess: 'Remover acesso',
    expires: 'expira',
    processing: 'Processamento de IA',
    processingIntro:
      'Escolha onde a IA da Poneva será executada para todas as pessoas na {company}: chats, voz, reuniões e agentes.',
    loading: 'Carregando…',
    retry: 'Tentar novamente',
    whatChanges: 'O que muda',
    currentChoice: 'Escolha atual',
    current: 'Atual',
    keepEu: 'Manter todo o processamento de IA na UE',
    useGlobal: 'Usar os melhores modelos do mundo',
    euBody:
      'Para regras rigorosas de proteção de dados. Alguns modelos e vozes mais recentes ainda não estão disponíveis na UE.',
    globalBody: 'Os modelos e vozes mais avançados, processados na UE ou em outros locais.',
    saveProcessing: 'Salvar',
    savingProcessing: 'Salvando…',
    processingSaved: 'Salvo',
    setupAria: 'Continue configurando seu espaço de trabalho',
    setupMoreTitle: 'Mais contexto. Mais possibilidades.',
    setupFirstTitle: 'Suas ferramentas tornam tudo isso mais útil.',
    setupConnected: 'Conexões: {count}. Traga outra parte do seu trabalho para a conversa.',
    setupFirstBody:
      'Conecte as ferramentas que você usa para trabalhar com suas próprias informações.',
    setupAdd: 'Adicionar outra conexão',
    setupFirst: 'Conectar sua primeira ferramenta',
    setupExplore: 'Explorar conexões disponíveis',
    setupDismiss: 'Dispensar sugestão de conexão',
    personalMemory: 'Memória pessoal',
    personalMemoryDescription:
      'Preferências e contexto específicos para você. O que toda a empresa deve saber pertence às páginas de Conhecimento.',
    preferencesSaved: 'Preferências salvas',
    companyInviteTitle: 'Configurações da empresa',
    companyInviteDescription:
      'Sua conta precisa de um convite da empresa. Entre em contato com a Poneva para conectar sua conta.',
  },
  ro: {
    companyProfile: 'Profilul companiei',
    profileUnavailable: 'Profilul companiei dvs. nu este disponibil.',
    profileDescription:
      'Contextul de pornire pentru toți cei care lucrează cu Poneva la {company}.',
    companyName: 'Numele companiei',
    website: 'Site web',
    primaryMarket: 'Piața principală',
    companyLanguage: 'Limba companiei',
    companyMatters: 'Ce contează pentru compania dvs.',
    profileHint: 'Un punct de pornire util, nu o limitare a lucrurilor pe care le puteți întreba.',
    reloadCompany: 'Reîncarcă informațiile companiei',
    saving: 'Se salvează…',
    saveCompanyProfile: 'Salvează profilul companiei',
    profileSaved: 'Profilul companiei a fost salvat',
    team: 'Echipă',
    teamDescription: 'Persoanele cu care lucrați și ceea ce pot publica.',
    loadTeam: 'Încarcă echipa',
    people: 'Persoane',
    member: 'Membru',
    role: 'Rol',
    companyPublishing: 'Publicarea în cadrul companiei',
    teamPrivacy:
      'Conversațiile și schițele rămân private până când sunt partajate. Permisiunea de publicare nu oferă niciodată acces la activitatea privată a altcuiva.',
    inviteColleague: 'Invită un coleg',
    inviteIntro: 'O invitație, o singură adresă de e-mail verificată.',
    emailAddress: 'Adresă de e-mail',
    invitationPlaceholder: 'coleg@companie.ro',
    admin: 'Administrator',
    createInvitation: 'Creează un link de invitație',
    invitationShare: 'Partajați linkul generat cu colegul dvs. Invitațiile expiră după șapte zile.',
    invitationLink: 'Link de invitație',
    publishingPermission: 'Permisiune de publicare',
    canPublish: 'Poate publica',
    needsApproval: 'Necesită aprobare',
    needsApprovalDefault: 'Necesită aprobare · implicit',
    canPublishDefault: 'Poate publica · implicit',
    save: 'Salvează',
    savePermissions: 'Salvează permisiunile pentru {email}',
    owner: 'Proprietar',
    transferOwnership: 'Transferă calitatea de proprietar',
    removeAccess: 'Elimină accesul',
    expires: 'expiră',
    processing: 'Procesarea AI',
    processingIntro:
      'Alegeți unde rulează AI-ul Poneva pentru toți cei de la {company}: conversații, voce, întâlniri și agenți.',
    loading: 'Se încarcă…',
    retry: 'Încearcă din nou',
    whatChanges: 'Ce se schimbă',
    currentChoice: 'Opțiunea actuală',
    current: 'Actuală',
    keepEu: 'Păstrează toată procesarea AI în UE',
    useGlobal: 'Folosește cele mai bune modele din întreaga lume',
    euBody:
      'Pentru reguli stricte privind datele. Câteva modele și voci mai noi nu sunt încă disponibile în UE.',
    globalBody: 'Cele mai puternice modele și voci, procesate în UE sau în altă parte.',
    saveProcessing: 'Salvează',
    savingProcessing: 'Se salvează…',
    processingSaved: 'Salvat',
    setupAria: 'Continuă să-ți configurezi spațiul de lucru',
    setupMoreTitle: 'Mai mult context. Mai multe posibilități.',
    setupFirstTitle: 'Instrumentele tale fac totul mai util.',
    setupConnected: 'Conexiuni: {count}. Adu o altă parte a muncii tale în conversație.',
    setupFirstBody:
      'Conectează instrumentele pe care le folosești pentru a lucra cu propriile informații.',
    setupAdd: 'Adaugă o altă conexiune',
    setupFirst: 'Conectează primul instrument',
    setupExplore: 'Explorează conexiunile disponibile',
    setupDismiss: 'Închide sugestia de conectare',
    personalMemory: 'Memorie personală',
    personalMemoryDescription:
      'Preferințe și context specifice ție. Informațiile pe care ar trebui să le cunoască întreaga companie aparțin paginilor sale de cunoștințe.',
    preferencesSaved: 'Preferințele au fost salvate',
    companyInviteTitle: 'Setările companiei',
    companyInviteDescription:
      'Contul dvs. are nevoie de o invitație din partea companiei. Contactați Poneva pentru a vă conecta contul.',
  },
  sk: {
    companyProfile: 'Profil spoločnosti',
    profileUnavailable: 'Profil vašej spoločnosti nie je k dispozícii.',
    profileDescription:
      'Východiskový kontext pre každého, kto pracuje s Poneva v spoločnosti {company}.',
    companyName: 'Názov spoločnosti',
    website: 'Webová stránka',
    primaryMarket: 'Primárny trh',
    companyLanguage: 'Jazyk spoločnosti',
    companyMatters: 'Na čom vašej spoločnosti záleží',
    profileHint: 'Užitočný východiskový bod, nie obmedzenie toho, na čo sa môžete pýtať.',
    reloadCompany: 'Znova načítať spoločnosť',
    saving: 'Ukladá sa…',
    saveCompanyProfile: 'Uložiť profil spoločnosti',
    profileSaved: 'Profil spoločnosti bol uložený',
    team: 'Tím',
    teamDescription: 'Ľudia, s ktorými pracujete, a obsah, ktorý môžu publikovať.',
    loadTeam: 'Načítať tím',
    people: 'Ľudia',
    member: 'Člen',
    role: 'Úloha',
    companyPublishing: 'Publikovanie v spoločnosti',
    teamPrivacy:
      'Chaty a koncepty zostávajú súkromné, kým ich nezdieľate. Povolenie na publikovanie nikdy neudeľuje prístup k súkromnej práci inej osoby.',
    inviteColleague: 'Pozvať kolegu',
    inviteIntro: 'Jedna pozvánka, jedna overená e-mailová adresa.',
    emailAddress: 'E-mailová adresa',
    invitationPlaceholder: 'kolega@spolocnost.sk',
    admin: 'Administrátor',
    createInvitation: 'Vytvoriť odkaz s pozvánkou',
    invitationShare:
      'Zdieľajte vygenerovaný odkaz s kolegom. Platnosť pozvánok vyprší po siedmich dňoch.',
    invitationLink: 'Odkaz s pozvánkou',
    publishingPermission: 'Povolenie na publikovanie',
    canPublish: 'Môže publikovať',
    needsApproval: 'Vyžaduje schválenie',
    needsApprovalDefault: 'Vyžaduje schválenie · predvolené',
    canPublishDefault: 'Môže publikovať · predvolené',
    save: 'Uložiť',
    savePermissions: 'Uložiť povolenia pre {email}',
    owner: 'Vlastník',
    transferOwnership: 'Previesť vlastníctvo',
    removeAccess: 'Odobrať prístup',
    expires: 'platnosť vyprší',
    processing: 'Spracovanie AI',
    processingIntro:
      'Vyberte, kde bude AI od Poneva fungovať pre všetkých v spoločnosti {company}: v chate, hlasových rozhovoroch, na poradách a v agentoch.',
    loading: 'Načítava sa…',
    retry: 'Skúsiť znova',
    whatChanges: 'Čo sa zmení',
    currentChoice: 'Aktuálna voľba',
    current: 'Aktuálne',
    keepEu: 'Ponechať všetko spracovanie AI v EÚ',
    useGlobal: 'Používať najlepšie modely na celom svete',
    euBody:
      'Pre prísne pravidlá ochrany údajov. Niekoľko novších modelov a hlasov zatiaľ nie je v EÚ k dispozícii.',
    globalBody: 'Najvýkonnejšie modely a hlasy spracúvané v EÚ alebo inde.',
    saveProcessing: 'Uložiť',
    savingProcessing: 'Ukladá sa…',
    processingSaved: 'Uložené',
    setupAria: 'Pokračovať vo vytváraní pracovného priestoru',
    setupMoreTitle: 'Viac kontextu. Viac možností.',
    setupFirstTitle: 'Vaše nástroje vám uľahčia prácu.',
    setupConnected: 'Pripojenia: {count}. Pridajte do konverzácie ďalšiu časť svojej práce.',
    setupFirstBody: 'Pripojte nástroje, ktoré používate na prácu s vlastnými informáciami.',
    setupAdd: 'Pridať ďalšie pripojenie',
    setupFirst: 'Pripojiť prvý nástroj',
    setupExplore: 'Preskúmať dostupné pripojenia',
    setupDismiss: 'Zavrieť návrh pripojenia',
    personalMemory: 'Osobná pamäť',
    personalMemoryDescription:
      'Predvoľby a kontext špecifické pre vás. To, čo by mala vedieť celá spoločnosť, patrí na jej stránky znalostí.',
    preferencesSaved: 'Predvoľby boli uložené',
    companyInviteTitle: 'Nastavenia spoločnosti',
    companyInviteDescription:
      'Váš účet potrebuje pozvánku do spoločnosti. Kontaktujte Poneva a prepojte svoj účet.',
  },
  sl: {
    companyProfile: 'Profil podjetja',
    profileUnavailable: 'Profil vašega podjetja ni na voljo.',
    profileDescription: 'Začetni kontekst za vse, ki delajo s Ponevo v podjetju {company}.',
    companyName: 'Ime podjetja',
    website: 'Spletno mesto',
    primaryMarket: 'Primarni trg',
    companyLanguage: 'Jezik podjetja',
    companyMatters: 'Kaj je pomembno vašemu podjetju',
    profileHint: 'Uporabno izhodišče, ne omejitev tega, kar lahko vprašate.',
    reloadCompany: 'Znova naloži podjetje',
    saving: 'Shranjevanje …',
    saveCompanyProfile: 'Shrani profil podjetja',
    profileSaved: 'Profil podjetja je shranjen',
    team: 'Ekipa',
    teamDescription: 'Ljudje, s katerimi delate, in vsebina, ki jo lahko objavljajo.',
    loadTeam: 'Naloži ekipo',
    people: 'Osebe',
    member: 'Član',
    role: 'Vloga',
    companyPublishing: 'Objavljanje v podjetju',
    teamPrivacy:
      'Klepeti in osnutki ostanejo zasebni, dokler jih ne delite. Dovoljenje za objavljanje nikoli ne omogoča dostopa do zasebnega dela druge osebe.',
    inviteColleague: 'Povabi sodelavca',
    inviteIntro: 'Eno povabilo, en potrjen e-poštni naslov.',
    emailAddress: 'E-poštni naslov',
    invitationPlaceholder: 'sodelavec@podjetje.si',
    admin: 'Skrbnik',
    createInvitation: 'Ustvari povezavo za povabilo',
    invitationShare: 'Ustvarjeno povezavo delite s sodelavcem. Povabila potečejo po sedmih dneh.',
    invitationLink: 'Povezava za povabilo',
    publishingPermission: 'Dovoljenje za objavljanje',
    canPublish: 'Lahko objavlja',
    needsApproval: 'Potrebna je odobritev',
    needsApprovalDefault: 'Potrebna je odobritev · privzeto',
    canPublishDefault: 'Lahko objavlja · privzeto',
    save: 'Shrani',
    savePermissions: 'Shrani dovoljenja za {email}',
    owner: 'Lastnik',
    transferOwnership: 'Prenesi lastništvo',
    removeAccess: 'Odstrani dostop',
    expires: 'poteče',
    processing: 'Obdelava z umetno inteligenco',
    processingIntro:
      'Izberite, kje bo umetna inteligenca Poneve delovala za vse v podjetju {company}: v klepetu, glasu, na sestankih in pri agentih.',
    loading: 'Nalaganje …',
    retry: 'Poskusi znova',
    whatChanges: 'Kaj se spremeni',
    currentChoice: 'Trenutna izbira',
    current: 'Trenutno',
    keepEu: 'Vso obdelavo z umetno inteligenco ohrani v EU',
    useGlobal: 'Uporabi najboljše modele po vsem svetu',
    euBody:
      'Za stroga pravila glede podatkov. Nekateri novejši modeli in glasovi v EU še niso na voljo.',
    globalBody: 'Najzmogljivejši modeli in glasovi, obdelani v EU ali drugje.',
    saveProcessing: 'Shrani',
    savingProcessing: 'Shranjevanje …',
    processingSaved: 'Shranjeno',
    setupAria: 'Nadaljujte z ustvarjanjem delovnega okolja',
    setupMoreTitle: 'Več konteksta. Več možnosti.',
    setupFirstTitle: 'Vaša orodja naredijo to še uporabnejše.',
    setupConnected: 'Povezave: {count}. V pogovor vključite še en del svojega dela.',
    setupFirstBody: 'Povežite orodja, ki jih uporabljate za delo z lastnimi informacijami.',
    setupAdd: 'Dodaj še eno povezavo',
    setupFirst: 'Poveži svoje prvo orodje',
    setupExplore: 'Razišči razpoložljive povezave',
    setupDismiss: 'Opusti predlog za povezavo',
    personalMemory: 'Osebni spomin',
    personalMemoryDescription:
      'Nastavitve in kontekst, značilni za vas. Kar mora vedeti celotno podjetje, spada na njegove strani znanja.',
    preferencesSaved: 'Nastavitve so shranjene',
    companyInviteTitle: 'Nastavitve podjetja',
    companyInviteDescription:
      'Vaš račun potrebuje povabilo v podjetje. Za povezavo računa se obrnite na Ponevo.',
  },
  es: {
    companyProfile: 'Perfil de la empresa',
    profileUnavailable: 'El perfil de tu empresa no está disponible.',
    profileDescription:
      'El contexto inicial para todas las personas que trabajan con Poneva en {company}.',
    companyName: 'Nombre de la empresa',
    website: 'Sitio web',
    primaryMarket: 'Mercado principal',
    companyLanguage: 'Idioma de la empresa',
    companyMatters: 'Qué le importa a tu empresa',
    profileHint: 'Un punto de partida útil, no una limitación sobre lo que puedes preguntar.',
    reloadCompany: 'Recargar empresa',
    saving: 'Guardando…',
    saveCompanyProfile: 'Guardar perfil de la empresa',
    profileSaved: 'Perfil de la empresa guardado',
    team: 'Equipo',
    teamDescription: 'Las personas con las que trabajas y lo que pueden publicar.',
    loadTeam: 'Cargar equipo',
    people: 'Personas',
    member: 'Miembro',
    role: 'Rol',
    companyPublishing: 'Publicación de la empresa',
    teamPrivacy:
      'Los chats y borradores permanecen privados hasta que se comparten. El permiso de publicación nunca da acceso al trabajo privado de otra persona.',
    inviteColleague: 'Invitar a un compañero',
    inviteIntro: 'Una invitación, una dirección de correo verificada.',
    emailAddress: 'Dirección de correo electrónico',
    invitationPlaceholder: 'colleague@company.com',
    admin: 'Administrador',
    createInvitation: 'Crear enlace de invitación',
    invitationShare:
      'Comparte el enlace generado con tu compañero. Las invitaciones caducan después de siete días.',
    invitationLink: 'Enlace de invitación',
    publishingPermission: 'Permiso de publicación',
    canPublish: 'Puede publicar',
    needsApproval: 'Necesita aprobación',
    needsApprovalDefault: 'Necesita aprobación · predeterminado',
    canPublishDefault: 'Puede publicar · predeterminado',
    save: 'Guardar',
    savePermissions: 'Guardar permisos para {email}',
    owner: 'Propietario',
    transferOwnership: 'Transferir propiedad',
    removeAccess: 'Eliminar acceso',
    expires: 'caduca',
    processing: 'Procesamiento de IA',
    processingIntro:
      'Elige dónde se ejecuta la IA de Poneva para todos en {company}: chats, voz, reuniones y agentes.',
    loading: 'Cargando…',
    retry: 'Intentar de nuevo',
    whatChanges: 'Qué cambia',
    currentChoice: 'Opción actual',
    current: 'Actual',
    keepEu: 'Mantener todo el procesamiento de IA en la UE',
    useGlobal: 'Usar los mejores modelos del mundo',
    euBody:
      'Para cumplir normas estrictas de protección de datos. Algunos modelos y voces más recientes aún no están disponibles en la UE.',
    globalBody: 'Los modelos y voces más avanzados, procesados en la UE o en otros lugares.',
    saveProcessing: 'Guardar',
    savingProcessing: 'Guardando…',
    processingSaved: 'Guardado',
    setupAria: 'Sigue configurando tu espacio de trabajo',
    setupMoreTitle: 'Más contexto. Más posibilidades.',
    setupFirstTitle: 'Tus herramientas hacen que esto sea más útil.',
    setupConnected: 'Conexiones: {count}. Incorpora otra parte de tu trabajo a la conversación.',
    setupFirstBody: 'Conecta las herramientas que usas para trabajar con tu propia información.',
    setupAdd: 'Añadir otra conexión',
    setupFirst: 'Conecta tu primera herramienta',
    setupExplore: 'Explorar conexiones disponibles',
    setupDismiss: 'Descartar sugerencia de conexión',
    personalMemory: 'Memoria personal',
    personalMemoryDescription:
      'Preferencias y contexto específicos para ti. Lo que debería saber toda la empresa pertenece a sus páginas de Conocimiento.',
    preferencesSaved: 'Preferencias guardadas',
    companyInviteTitle: 'Configuración de la empresa',
    companyInviteDescription:
      'Tu cuenta necesita una invitación de empresa. Contacta con Poneva para conectar tu cuenta.',
  },
  sv: {
    companyProfile: 'Företagsprofil',
    profileUnavailable: 'Din företagsprofil är inte tillgänglig.',
    profileDescription: 'Startkontexten för alla som arbetar med Poneva på {company}.',
    companyName: 'Företagsnamn',
    website: 'Webbplats',
    primaryMarket: 'Huvudmarknad',
    companyLanguage: 'Företagsspråk',
    companyMatters: 'Vad är viktigt för ditt företag',
    profileHint: 'En användbar utgångspunkt, inte en begränsning av vad du kan fråga om.',
    reloadCompany: 'Ladda om företaget',
    saving: 'Sparar…',
    saveCompanyProfile: 'Spara företagsprofil',
    profileSaved: 'Företagsprofilen har sparats',
    team: 'Team',
    teamDescription: 'Personerna du arbetar med och vad de kan publicera.',
    loadTeam: 'Läs in teamet',
    people: 'Personer',
    member: 'Medlem',
    role: 'Roll',
    companyPublishing: 'Företagspublicering',
    teamPrivacy:
      'Chattar och utkast förblir privata tills de delas. Publiceringsbehörighet ger aldrig åtkomst till någon annans privata arbete.',
    inviteColleague: 'Bjud in en kollega',
    inviteIntro: 'En inbjudan, en verifierad e-postadress.',
    emailAddress: 'E-postadress',
    invitationPlaceholder: 'colleague@company.com',
    admin: 'Administratör',
    createInvitation: 'Skapa inbjudningslänk',
    invitationShare:
      'Dela den genererade länken med din kollega. Inbjudningar upphör att gälla efter sju dagar.',
    invitationLink: 'Inbjudningslänk',
    publishingPermission: 'Publiceringsbehörighet',
    canPublish: 'Kan publicera',
    needsApproval: 'Kräver godkännande',
    needsApprovalDefault: 'Kräver godkännande · standard',
    canPublishDefault: 'Kan publicera · standard',
    save: 'Spara',
    savePermissions: 'Spara behörigheter för {email}',
    owner: 'Ägare',
    transferOwnership: 'Överför ägarskap',
    removeAccess: 'Ta bort åtkomst',
    expires: 'upphör att gälla',
    processing: 'AI-bearbetning',
    processingIntro:
      'Välj var Ponevas AI körs för alla på {company}: chatt, röst, möten och agenter.',
    loading: 'Läser in…',
    retry: 'Försök igen',
    whatChanges: 'Vad ändras',
    currentChoice: 'Aktuellt val',
    current: 'Aktuellt',
    keepEu: 'Håll all AI-bearbetning inom EU',
    useGlobal: 'Använd de bästa modellerna i hela världen',
    euBody:
      'För strikta dataskyddsregler. Några nyare modeller och röster är ännu inte tillgängliga inom EU.',
    globalBody: 'De bästa modellerna och rösterna, med bearbetning inom eller utanför EU.',
    saveProcessing: 'Spara',
    savingProcessing: 'Sparar…',
    processingSaved: 'Sparat',
    setupAria: 'Fortsätt bygga din arbetsyta',
    setupMoreTitle: 'Mer kontext. Fler möjligheter.',
    setupFirstTitle: 'Dina verktyg gör detta mer användbart.',
    setupConnected:
      'Anslutningar: {count}. Ta med ytterligare en del av ditt arbete i konversationen.',
    setupFirstBody: 'Anslut verktygen du använder för att arbeta med din egen information.',
    setupAdd: 'Lägg till ännu en anslutning',
    setupFirst: 'Anslut ditt första verktyg',
    setupExplore: 'Utforska tillgängliga anslutningar',
    setupDismiss: 'Avfärda förslag om anslutning',
    personalMemory: 'Personligt minne',
    personalMemoryDescription:
      'Inställningar och kontext som är specifika för dig. Det som hela företaget bör känna till hör hemma på företagets kunskapssidor.',
    preferencesSaved: 'Inställningarna har sparats',
    companyInviteTitle: 'Företagsinställningar',
    companyInviteDescription:
      'Ditt konto behöver en företagsinbjudan. Kontakta Poneva för att ansluta ditt konto.',
  },
} as const;

export type CompanySettingsCopyKey = keyof typeof companySettingsCopy.en;
export default companySettingsCopy as unknown as NamespaceCatalog<CompanySettingsCopyKey>;
